import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import useHeartbeat from '../hooks/useHeartbeat'
import Button from '../components/ui/Button'
import ThemeToggle from '../components/ui/ThemeToggle'
import TextTool from '../components/ui/TextTool'
import ColorTools from '../components/ColorTools'
import ChatFooterStats from '../components/ui/ChatFooterStats'
import ZennaStatsBar from '../components/ZennaStatsBar'
import PatternPdfModal from '../components/PatternPdfModal'
import CraftConverter from '../components/CraftConverter'
import CanvasEditor from '../components/CanvasEditor'
import StitchPreview from '../components/ui/StitchPreview'
import EmbroideryEditor from '../components/EmbroideryEditor'
import EmbroideryCleanupReview from '../components/EmbroideryCleanupReview'
import VersionHistoryPanel from '../components/VersionHistoryPanel'
import PromptModal from '../components/PromptModal'
import QrShareModal from '../components/QrShareModal'
import TemplateLibrary from '../components/TemplateLibrary'
import ThreeDViewer from '../components/ThreeDViewer'

const API_URL = import.meta.env.VITE_API_URL || ''

// =============================================================================
// COST ESTIMATE PANEL COMPONENT
// =============================================================================
const CostEstimatePanel = ({ isOpen, onClose, data }) => {
  if (!isOpen || !data) return null

  const copyEstimate = () => {
    let text = `📊 Project Cost Estimate\n`
    text += `Craft: ${data.craft_type} | Qty: ${data.quantity}\n\n`
    text += `MATERIALS:\n`
    data.materials.forEach(m => {
      text += `  • ${m.item}: $${m.cost.toFixed(2)}\n`
    })
    text += `\nTOOLS NEEDED:\n`
    data.tools_needed.forEach(t => {
      text += `  • ${t.item}: $${t.cost.toFixed(2)}${t.essential ? ' (essential)' : ' (optional)'}\n`
    })
    text += `\n─────────────────\n`
    text += `Materials: $${data.total_materials_cost.toFixed(2)}\n`
    text += `Tools: $${data.total_tools_cost.toFixed(2)}\n`
    text += `TOTAL: $${data.total_estimated.toFixed(2)}\n`
    if (data.quantity > 1) {
      text += `Per Unit: $${data.per_unit_cost.toFixed(2)}\n`
    }
    navigator.clipboard.writeText(text)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white dark:bg-slate-800 rounded-2xl max-w-lg w-full shadow-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-zenna-emerald to-emerald-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-display font-bold text-lg">Cost Estimate</h3>
              <p className="text-white/80 text-xs capitalize">{data.craft_type} • Qty: {data.quantity}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {/* Materials Section */}
          <div>
            <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="w-5 h-5 bg-blue-100 dark:bg-blue-900/50 rounded flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs">🧵</span>
              Materials
            </h4>
            <div className="space-y-2">
              {data.materials.map((mat, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-50 dark:bg-slate-700/50 rounded-xl px-4 py-3">
                  <div className="flex-1">
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{mat.item}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-slate-800 dark:text-white">${mat.cost.toFixed(2)}</span>
                    {mat.shopLink && (
                      <a
                        href={mat.shopLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 px-2.5 py-1 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900 transition-colors font-medium flex items-center gap-1"
                      >
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M21.822 7.431A1 1 0 0021 7H7.333L6.179 4.23A1.994 1.994 0 004.333 3H2v2h2.333l4.744 11.385A1 1 0 0010 17h8c.417 0 .79-.259.937-.648l3-8a1 1 0 00-.115-.921z" />
                          <circle cx="10.5" cy="20" r="1.5" />
                          <circle cx="17.5" cy="20" r="1.5" />
                        </svg>
                        Shop
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tools Section */}
          {data.tools_needed.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <span className="w-5 h-5 bg-purple-100 dark:bg-purple-900/50 rounded flex items-center justify-center text-purple-600 dark:text-purple-400 text-xs">🔧</span>
                Tools Needed
              </h4>
              <div className="space-y-2">
                {data.tools_needed.map((tool, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-50 dark:bg-slate-700/50 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{tool.item}</span>
                      {tool.essential ? (
                        <span className="text-[10px] bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded font-medium">Required</span>
                      ) : (
                        <span className="text-[10px] bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded font-medium">Optional</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-slate-800 dark:text-white">${tool.cost.toFixed(2)}</span>
                      {tool.shopLink && (
                        <a
                          href={tool.shopLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 px-2.5 py-1 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900 transition-colors font-medium flex items-center gap-1"
                        >
                          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M21.822 7.431A1 1 0 0021 7H7.333L6.179 4.23A1.994 1.994 0 004.333 3H2v2h2.333l4.744 11.385A1 1 0 0010 17h8c.417 0 .79-.259.937-.648l3-8a1 1 0 00-.115-.921z" />
                            <circle cx="10.5" cy="20" r="1.5" />
                            <circle cx="17.5" cy="20" r="1.5" />
                          </svg>
                          Shop
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Budget Alternatives */}
          {data.budget_alternatives.length > 0 && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 mb-2 flex items-center gap-1">
                💡 Budget Tips
              </h4>
              <ul className="space-y-1">
                {data.budget_alternatives.map((tip, i) => (
                  <li key={i} className="text-xs text-emerald-600 dark:text-emerald-300 flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Notes */}
          {data.notes.length > 0 && (
            <div className="space-y-1">
              {data.notes.map((note, i) => (
                <p key={i} className="text-[11px] text-slate-400 dark:text-slate-500 italic">{note}</p>
              ))}
            </div>
          )}
        </div>

        {/* Footer Totals */}
        <div className="border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 px-6 py-4 flex-shrink-0">
          <div className="space-y-1.5 mb-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500 dark:text-slate-400">Materials</span>
              <span className="text-slate-700 dark:text-slate-300">${data.total_materials_cost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500 dark:text-slate-400">Tools (essential)</span>
              <span className="text-slate-700 dark:text-slate-300">${data.total_tools_cost.toFixed(2)}</span>
            </div>
            <div className="h-px bg-slate-200 dark:bg-slate-700 my-1"></div>
            <div className="flex justify-between text-base font-bold">
              <span className="text-slate-800 dark:text-white">Total Estimate</span>
              <span className="text-zenna-emerald">${data.total_estimated.toFixed(2)}</span>
            </div>
            {data.quantity > 1 && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Per unit cost</span>
                <span className="text-zenna-violet font-semibold">${data.per_unit_cost.toFixed(2)}/each</span>
              </div>
            )}
          </div>
          <button
            onClick={copyEstimate}
            className="w-full py-2.5 bg-gradient-zenna text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
            </svg>
            Copy Estimate
          </button>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// SOCIAL POST PANEL COMPONENT
// =============================================================================
const SocialPostPanel = ({ isOpen, onClose, data }) => {
  const [copiedIndex, setCopiedIndex] = useState(null)
  const [hashtagsCopied, setHashtagsCopied] = useState(false)
  const [showPhotoTips, setShowPhotoTips] = useState(false)

  if (!isOpen || !data) return null

  const platformConfig = {
    instagram: { icon: '📸', color: 'from-pink-500 to-purple-600', label: 'Instagram' },
    tiktok: { icon: '🎵', color: 'from-slate-800 to-slate-900 dark:from-slate-200 dark:to-white', label: 'TikTok', darkText: true },
    pinterest: { icon: '📌', color: 'from-red-500 to-red-700', label: 'Pinterest' },
    facebook: { icon: '👥', color: 'from-blue-500 to-blue-700', label: 'Facebook' },
    all: { icon: '🌐', color: 'from-zenna-violet to-zenna-cyan', label: 'All Platforms' }
  }

  const platform = platformConfig[data.platform] || platformConfig.all

  const copyCaption = (caption, index) => {
    navigator.clipboard.writeText(caption)
    setCopiedIndex(index)
    setTimeout(() => setCopiedIndex(null), 2000)
  }

  const copyHashtags = () => {
    navigator.clipboard.writeText(data.hashtags.join(' '))
    setHashtagsCopied(true)
    setTimeout(() => setHashtagsCopied(false), 2000)
  }

  const copyAll = (index) => {
    const text = `${data.captions[index]}\n\n${data.hashtags.join(' ')}`
    navigator.clipboard.writeText(text)
    setCopiedIndex(index)
    setTimeout(() => setCopiedIndex(null), 2000)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white dark:bg-slate-800 rounded-2xl max-w-lg w-full shadow-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`bg-gradient-to-r ${platform.color} px-6 py-4 flex items-center justify-between flex-shrink-0`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center text-xl">
              {platform.icon}
            </div>
            <div>
              <h3 className={`font-display font-bold text-lg ${platform.darkText ? 'text-slate-900 dark:text-white' : 'text-white'}`}>
                Social Post Ready
              </h3>
              <p className={`text-xs ${platform.darkText ? 'text-slate-600 dark:text-white/80' : 'text-white/80'}`}>
                {platform.label} • {data.captions.length} caption{data.captions.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button onClick={onClose} className={`${platform.darkText ? 'text-slate-400 hover:text-slate-600 dark:text-white/80 dark:hover:text-white' : 'text-white/80 hover:text-white'} transition-colors`}>
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {/* Caption Options */}
          <div>
            <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
              ✏️ Caption Options
            </h4>
            <div className="space-y-3">
              {data.captions.map((caption, i) => (
                <div key={i} className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 group">
                  <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap mb-3 leading-relaxed">{caption}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => copyCaption(caption, i)}
                      className="text-xs bg-white dark:bg-slate-600 text-slate-600 dark:text-slate-300 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-500 hover:border-zenna-violet dark:hover:border-zenna-cyan transition-colors font-medium flex items-center gap-1.5"
                    >
                      {copiedIndex === i ? (
                        <>
                          <svg className="w-3 h-3 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Copied!
                        </>
                      ) : (
                        <>
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Copy Caption
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => copyAll(i)}
                      className="text-xs bg-gradient-zenna text-white px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity font-medium flex items-center gap-1.5"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                      Caption + Tags
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Hashtags */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                # Hashtags ({data.hashtags.length})
              </h4>
              <button
                onClick={copyHashtags}
                className="text-xs text-zenna-violet dark:text-zenna-cyan font-medium hover:underline flex items-center gap-1"
              >
                {hashtagsCopied ? (
                  <>✓ Copied!</>
                ) : (
                  <>
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy All
                  </>
                )}
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {data.hashtags.map((tag, i) => (
                <span
                  key={i}
                  className="text-xs bg-zenna-violet/10 dark:bg-zenna-violet/20 text-zenna-violet dark:text-zenna-cyan px-2.5 py-1 rounded-lg font-medium cursor-pointer hover:bg-zenna-violet/20 dark:hover:bg-zenna-violet/30 transition-colors"
                  onClick={() => { navigator.clipboard.writeText(tag) }}
                  title="Click to copy"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Photography Tips */}
          {data.photography_tips && data.photography_tips.length > 0 && (
            <div>
              <button
                onClick={() => setShowPhotoTips(!showPhotoTips)}
                className="w-full flex items-center justify-between text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
              >
                <span className="flex items-center gap-2">📷 Photography Tips</span>
                <svg className={`w-4 h-4 transition-transform ${showPhotoTips ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showPhotoTips && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 space-y-2">
                  {data.photography_tips.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-blue-700 dark:text-blue-300">
                      <span className="mt-0.5 text-blue-400">📌</span>
                      <span>{tip}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Best Posting Times */}
          {data.best_posting_times && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3 flex items-start gap-2">
              <span className="text-amber-500 mt-0.5">⏰</span>
              <p className="text-xs text-amber-700 dark:text-amber-300 font-medium">{data.best_posting_times}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}


// =============================================================================
// SIMPLE MARKDOWN RENDERER FOR CHAT MESSAGES
// =============================================================================
const renderMessageContent = (content) => {
  if (!content) return null

  let processed = content.replace(/\]\s*\n\s*\(/g, '](')
  processed = processed.replace(/\[([^\]]+)\]\s*\n\s*(https?:\/\/[^\s)]+)\)?/g, '[$1]($2)')

  // Extract quick action buttons [ACTION:label|action_type|params]
  const actionRegex = /\[ACTION:([^|]+)\|([^|]+)\|([^\]]*)\]/g
  const actions = []
  let actionMatch
  while ((actionMatch = actionRegex.exec(processed)) !== null) {
    actions.push({ label: actionMatch[1], type: actionMatch[2], params: actionMatch[3] })
  }
  // Remove action tags from displayed text
  processed = processed.replace(actionRegex, '')

  const lines = processed.split('\n')
  const elements = []

  lines.forEach((line, lineIndex) => {
    if (/^#{1,3}\s/.test(line)) {
      const level = line.match(/^(#{1,3})/)[1].length
      const text = line.replace(/^#{1,3}\s*/, '')
      const Tag = level === 1 ? 'h3' : level === 2 ? 'h4' : 'h5'
      const sizes = { 1: 'text-base font-bold', 2: 'text-sm font-bold', 3: 'text-sm font-semibold' }
      elements.push(
        <Tag key={lineIndex} className={`${sizes[level]} mt-2 mb-1`}>
          {renderInlineMarkdown(text)}
        </Tag>
      )
      return
    }

    if (/^\s*[•\-\*]\s/.test(line)) {
      const text = line.replace(/^\s*[•\-\*]\s*/, '')
      elements.push(
        <div key={lineIndex} className="flex items-start gap-1.5 ml-1 my-0.5">
          <span className="mt-0.5 text-zenna-violet dark:text-zenna-cyan text-xs flex-shrink-0">•</span>
          <span>{renderInlineMarkdown(text)}</span>
        </div>
      )
      return
    }

    if (line.trim() === '') {
      elements.push(<div key={lineIndex} className="h-1.5" />)
      return
    }

    elements.push(
      <p key={lineIndex} className="my-0.5">{renderInlineMarkdown(line)}</p>
    )
  })

  return (
    <div className="text-sm whitespace-pre-wrap">
      {elements}
      {actions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
          {actions.map((action, i) => (
            <button key={i} data-action-type={action.type} data-action-params={action.params}
              className="quick-action-btn inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full bg-gradient-to-r from-zenna-violet/10 to-zenna-cyan/10 text-zenna-violet hover:from-zenna-violet/20 hover:to-zenna-cyan/20 transition-all border border-zenna-violet/20 hover:border-zenna-violet/40 cursor-pointer"
              onClick={(e) => {
                const evt = new CustomEvent('zenna-quick-action', { detail: { type: action.type, params: action.params, label: action.label } })
                window.dispatchEvent(evt)
              }}>
              {action.type === 'generate' && <span>✨</span>}
              {action.type === 'open_canvas' && <span>🎨</span>}
              {action.type === 'load_template' && <span>📋</span>}
              {action.type === 'add_to_canvas' && <span>➕</span>}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const renderInlineMarkdown = (text) => {
  if (!text) return text

  const parts = []
  let remaining = text
  let key = 0

  while (remaining.length > 0) {
    const patterns = [
      { regex: /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/, type: 'link' },
      { regex: /(Export\s*.{0,3}\s*SVG|Export\s*.{0,3}\s*PNG|Export\s*.{0,3}\s*PDF|Export\s*.{0,3}\s*Pattern|click\s+Export|hit\s+Export|Export\s+menu|Export\s+button|Export\s+as\s+\w+)/i, type: 'export-link' },
      { regex: /\*\*([^*]+)\*\*/, type: 'bold' },
      { regex: /\*([^*]+)\*/, type: 'italic' },
    ]

    let earliest = null
    let earliestIndex = remaining.length

    for (const pattern of patterns) {
      const match = remaining.match(pattern.regex)
      if (match && match.index < earliestIndex) {
        earliest = { match, type: pattern.type }
        earliestIndex = match.index
      }
    }

    if (!earliest) {
      parts.push(remaining)
      break
    }

    if (earliestIndex > 0) {
      parts.push(remaining.slice(0, earliestIndex))
    }

    const { match, type } = earliest

    if (type === 'export-link') {
      parts.push(
        <button
          key={`export-${key++}`}
          onClick={() => window.dispatchEvent(new CustomEvent('zenna-open-export'))}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors cursor-pointer border border-emerald-500/30"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
          {match[0]}
        </button>
      )
    } else if (type === 'link') {
      parts.push(
        <a
          key={`link-${key++}`}
          href={match[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-zenna-violet dark:text-zenna-cyan underline underline-offset-2 hover:opacity-80 transition-opacity inline-flex items-center gap-0.5"
        >
          {match[1]}
          <svg className="w-3 h-3 inline flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      )
    } else if (type === 'bold') {
      parts.push(<strong key={`bold-${key++}`} className="font-semibold">{match[1]}</strong>)
    } else if (type === 'italic') {
      parts.push(<em key={`italic-${key++}`}>{match[1]}</em>)
    }

    remaining = remaining.slice(earliestIndex + match[0].length)
  }

  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts
}


// =============================================================================
// MAIN WORKSPACE COMPONENT
// =============================================================================
const Workspace = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  
  // Heartbeat tracking
  useHeartbeat(user?.email, 'workspace')
  
  // Canvas Editor ref
  const canvasEditorRef = useRef(null)
  const pendingCanvasImage = useRef(null) // stores image URL when canvas isn't ready yet
  
  // Project state
  const [projectId, setProjectId] = useState(null)
  const [projectName, setProjectName] = useState('')
  const [showRenameModal, setShowRenameModal] = useState(false)
  const [isAutoSaving, setIsAutoSaving] = useState(false)
  
  // File upload state (images, PDFs, text docs — multi-file)
  const [uploadedFiles, setUploadedFiles] = useState([])
  const fileInputRef = useRef(null)
  
  // Color Tools state
  const [showColorTools, setShowColorTools] = useState(false)
  const [colorToolsTab, setColorToolsTab] = useState('threads')
  const [colorToolsColors, setColorToolsColors] = useState([])
  const [designColors, setDesignColors] = useState([])
  
  // Pattern PDF state
  const [patternPdfData, setPatternPdfData] = useState(null)
  const [showPatternPdfModal, setShowPatternPdfModal] = useState(false)

  // Craft converter state
  const [showCraftConverter, setShowCraftConverter] = useState(false)

  // Cost Estimate state
  const [costEstimateData, setCostEstimateData] = useState(null)
  const [showCostEstimate, setShowCostEstimate] = useState(false)

  // Social Post state
  const [socialPostData, setSocialPostData] = useState(null)
  const [showSocialPost, setShowSocialPost] = useState(false)

  // Template Library state
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false)

  // Canvas tracking state
  const [canvasHasContent, setCanvasHasContent] = useState(false)
  const [canvasImage, setCanvasImage] = useState(null) // last loaded image URL for server-side exports
  const [pendingEmbroiderySettings, setPendingEmbroiderySettings] = useState(null)
  const [showEmbroideryDialog, setShowEmbroideryDialog] = useState(false)
  const [pendingEmbroideryFormat, setPendingEmbroideryFormat] = useState(null)
  const [stitchPreview, setStitchPreview] = useState(null) // { previewData, pesBase64, filename }
  const [embroideryEditor, setEmbroideryEditor] = useState(null) // { previewData, pesBase64, filename, imageUrl }
  // Cleanup-review modal state. Shown between upload and stitch generation
  // so the user can confirm what was detected as bg/noise/threads. While
  // this is non-null, the modal is rendered.
  const [cleanupReview, setCleanupReview] = useState(null)
  // {
  //   cleanedImageBase64, originalImageUrl, cropBbox, cleanedSize,
  //   detectedColors, decisions,
  //   exportFormat, exportSettings, // need to remember to pass to digitizer
  //   isProcessing,                 // true while waiting for digitization
  // }
  const abortControllerRef = useRef(null)
  const [canvasSize, setCanvasSize] = useState(null) // original template dimensions for full-res export

  const [messages, setMessages] = useState([
    {
      id: 1,
      role: 'assistant',
      content: "Hi! I'm Zenna, your interactive craft companion. ✨\n\nDescribe what you'd like to create, and I'll help you design it. You can ask for anything - logos, patterns, text designs, or just brainstorm ideas with me.\n\n📷 You can also upload an image and I'll help you work with it!\n\nWhat shall we make today?",
      images: []
    }
  ])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const recognitionRef = useRef(null)
  const [currentCraftType, setCurrentCraftType] = useState(null) // Tracks what craft Zenna is working on
  const currentCraftTypeRef = useRef(null)
  useEffect(() => { currentCraftTypeRef.current = currentCraftType }, [currentCraftType])
  const [copiedMessageId, setCopiedMessageId] = useState(null)
  const [lastGalleryPostId, setLastGalleryPostId] = useState(null)
  const [selectedImageIndex, setSelectedImageIndex] = useState(null)
  const [mobileView, setMobileView] = useState('chat')
  const [pastHistory, setPastHistory] = useState([])
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [showVersionHistory, setShowVersionHistory] = useState(false)
  const [promptState, setPromptState] = useState(null) // { title, description, fields, onSubmit }
  const [qrShareState, setQrShareState] = useState(null) // { platform, imageUrl, caption, shareUrl }
  const [isExporting, setIsExporting] = useState(false)
  const [showTextTool, setShowTextTool] = useState(false)
  const [textToolSuggestions, setTextToolSuggestions] = useState(null)
  const [viewMode, setViewMode] = useState('2d')         // '2d', 'embroidery', or '3d'
  const [threeDMode, setThreeDMode] = useState('viewer')  // viewer/cookie-cutter/shadow-box/book-nook
  const [chatPanelWidth, setChatPanelWidth] = useState(384) // default w-96 = 384px
  const messagesEndRef = useRef(null)
  const chatScrollRef = useRef(null)
  const [showScrollBtn, setShowScrollBtn] = useState(false)
  const inputRef = useRef(null)

  // Cycling thinking messages
  const thinkingMessages = [
    "Sketching ideas...", "Mixing colors...", "Crafting magic...", "Finding inspiration...",
    "Warming up the creative engines...", "Brainstorming possibilities...", "Threading the needle...",
    "Brewing up some magic...", "Channeling craft vibes...", "Preparing something special...",
    "Consulting the craft spirits...", "Waving the creative wand...", "Sprinkling pixie dust...",
    "Summoning the muse...", "Casting a design spell...", "Reading the creative runes...",
    "Gathering enchanted threads...", "Mixing potions of color...", "Awakening the imagination...",
    "Opening the wardrobe to creativity...", "To infinity and beyond the canvas...",
    "Following the yellow brick road...", "Wishing upon a design star...",
    "Finding Nemo... I mean, finding inspiration...", "Letting it go and creating...",
    "Believing in fairies and fonts...", "Painting with all the colors...",
    "Making dreams come true...", "Going to a galaxy of ideas...",
    "Assembling the Avengers of art...", "Untangling creative threads...",
    "Heating up the glue gun of genius...", "Calibrating the craft-o-matic...",
    "Downloading inspiration from the cloud...", "Sharpening the creative scissors...",
    "Firing up the imagination kiln...", "Steeping creativity like fine tea...",
    "Tuning the artistic frequency...", "Polishing the creative gems...",
    "Weaving a tapestry of ideas...", "brewing up some magic..."
  ]
  const [thinkingIndex, setThinkingIndex] = useState(0)
  
  useEffect(() => {
    if (!isLoading) { setThinkingIndex(0); return }
    const interval = setInterval(() => {
      setThinkingIndex(prev => (prev + 1) % thinkingMessages.length)
    }, 2000)
    return () => clearInterval(interval)
  }, [isLoading])

  // Load project if ID in URL
  useEffect(() => {
    const loadProjectId = searchParams.get('project')
    if (loadProjectId && user?.email) {
      loadProject(loadProjectId)
    }
  }, [searchParams, user])

  // Handle remix from gallery
  useEffect(() => {
    const isRemix = searchParams.get('remix')
    const remixImage = searchParams.get('remixImage')
    if (isRemix && remixImage) {
      const remixCraft = searchParams.get('remixCraft') || ''
      const remixTitle = searchParams.get('remixTitle') || 'Remix Project'
      const imageUrl = decodeURIComponent(remixImage)
      setCanvasImage(imageUrl)
      setCanvasHasContent(true)
      setProjectName(decodeURIComponent(remixTitle))
      if (remixCraft) setCurrentCraftType(decodeURIComponent(remixCraft))

      // Actually load the image onto the Fabric.js canvas with retry
      const loadRemixImage = async () => {
        for (let attempt = 0; attempt < 15; attempt++) {
          if (canvasEditorRef.current?.setMainImage) {
            try {
              await canvasEditorRef.current.setMainImage(imageUrl)
              console.log('Remix image loaded onto canvas')
              return
            } catch (err) {
              console.warn(`Remix canvas load attempt ${attempt + 1} failed:`, err)
            }
          }
          await new Promise(r => setTimeout(r, 400))
        }
        console.warn('Could not load remix image onto canvas after 15 attempts')
      }
      loadRemixImage()

      setMessages([{
        role: 'assistant',
        content: `I've loaded a design from the gallery onto your canvas! This is a remix of a ${remixCraft || 'craft'} design. You can:\n\n- Ask me to recreate it in a different style or craft type\n- Use it as inspiration for something new\n- Edit it directly on the canvas\n\nWhat would you like to do with this design?`
      }])
      // Clean up URL params
      setSearchParams(prev => {
        prev.delete('remix')
        prev.delete('remixImage')
        prev.delete('remixCraft')
        prev.delete('remixTitle')
        return prev
      })
    }
  }, [searchParams, user])

  // Handle quick-start from Dashboard (?startPrompt=...&startCraft=...)
  useEffect(() => {
    const startPrompt = searchParams.get('startPrompt')
    const startCraft = searchParams.get('startCraft')
    if (startPrompt || startCraft) {
      if (startPrompt) setInput(decodeURIComponent(startPrompt))
      if (startCraft) setCurrentCraftType(decodeURIComponent(startCraft))
      setSearchParams(prev => {
        prev.delete('startPrompt')
        prev.delete('startCraft')
        return prev
      })
    }
  }, [searchParams])

  // Load Zenna's memory of past conversations
  useEffect(() => {
    const loadPastHistory = async () => {
      if (!user?.email) return
      try {
        const response = await fetch(`${API_URL}/chat/history/${encodeURIComponent(user.email)}?limit=150`)
        if (response.ok) {
          const data = await response.json()
          setPastHistory(data.history || [])
          console.log(`Loaded ${data.history?.length || 0} messages into Zenna's memory`)
        }
      } catch (error) {
        console.error('Failed to load past history:', error)
      }
    }
    loadPastHistory()
  }, [user?.email])
  
  // Handle paste for images (Ctrl+V) — only in chat area, not canvas
  useEffect(() => {
    const handlePaste = async (e) => {
      // Don't intercept if canvas editor has focus
      if (e.target.closest('.canvas-editor-area')) return
      
      const items = e.clipboardData?.items
      if (!items) return
      
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault()
          const file = item.getAsFile()
          if (!file) continue
          if (file.size > 10 * 1024 * 1024) { alert('Pasted image must be under 10MB'); return }
          
          const reader = new FileReader()
          reader.onload = async () => {
            const rawDataUrl = reader.result
            const dataUrl = await compressImage(rawDataUrl, 1024, 0.85)
            const base64 = dataUrl.split(',')[1]
            const mediaType = dataUrl.startsWith('data:image/png') ? 'image/png' : 'image/jpeg'
            setUploadedFiles(prev => [...prev, {
              base64, mediaType, name: 'pasted-image.png',
              dataUrl, category: 'image'
            }])
            inputRef.current?.focus()
          }
          reader.readAsDataURL(file)
          break
        }
      }
    }
    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [])

  // Auto-save message function
  const autoSaveMessage = useCallback(async (message, currentProjectId = null) => {
    if (!user?.email) return currentProjectId
    
    try {
      setIsAutoSaving(true)
      const response = await fetch(`${API_URL}/chat/save-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: currentProjectId || projectId,
          email: user.email,
          message: { role: message.role, content: message.content, images: message.images || [] }
        })
      })
      
      if (!response.ok) {
        console.error('Auto-save failed')
        return currentProjectId || projectId
      }
      
      const data = await response.json()
      
      if (data.projectId && !projectId) {
        setProjectId(data.projectId)
        setProjectName('Untitled Project')
        window.history.replaceState({}, '', `/workspace?project=${data.projectId}`)
      }
      
      return data.projectId
    } catch (error) {
      console.error('Auto-save error:', error)
      return currentProjectId || projectId
    } finally {
      setIsAutoSaving(false)
    }
  }, [user?.email, projectId])

  // Auto-save canvas state (now includes Fabric.js JSON)
  const autoSaveCanvas = useCallback(async (imageUrl, index) => {
    if (!user?.email || !projectId) return
    
    try {
      const canvasJson = canvasEditorRef.current?.toJSON() || null
      
      await fetch(`${API_URL}/chat/project/${projectId}/canvas`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          canvasData: {
            currentImage: imageUrl,
            selectedIndex: index,
            fabricJson: canvasJson,
            craftType: currentCraftTypeRef.current,
          }
        })
      })
    } catch (error) {
      console.error('Canvas auto-save error:', error)
    }
  }, [user?.email, projectId])

  const loadProject = async (id) => {
    try {
      const response = await fetch(`${API_URL}/projects/${id}?email=${encodeURIComponent(user.email)}`)
      if (!response.ok) throw new Error('Failed to load project')
      
      const data = await response.json()
      
      setProjectId(data.project.id)
      setProjectName(data.project.name)
      
      // Restore craft type from saved project data
      if (data.project.canvas_data?.craftType) {
        setCurrentCraftType(data.project.canvas_data.craftType)
      }
      
      // Restore Fabric.js canvas from saved JSON (preferred) or fallback to single image
      if (data.project.canvas_data?.fabricJson && canvasEditorRef.current) {
        canvasEditorRef.current.loadFromJSON(data.project.canvas_data.fabricJson)
        setCanvasHasContent(true)
        if (data.project.canvas_data.currentImage) {
          setCanvasImage(data.project.canvas_data.currentImage)
          // Re-apply the selected image ON TOP of fabricJson to fix stale canvas state
          // (fabricJson may have been saved before a new image selection completed)
          try {
            await canvasEditorRef.current.setMainImage(data.project.canvas_data.currentImage)
          } catch (e) {
            console.warn('Restore image failed, using fabricJson state:', e)
          }
        }
      } else if (data.project.canvas_data?.currentImage) {
        setCanvasImage(data.project.canvas_data.currentImage)
        if (canvasEditorRef.current) {
          canvasEditorRef.current.setMainImage(data.project.canvas_data.currentImage)
          setCanvasHasContent(true)
        }
        if (data.project.canvas_data?.selectedIndex !== undefined) {
          setSelectedImageIndex(data.project.canvas_data.selectedIndex)
        }
      } else if (data.project.images?.length > 0) {
        setCanvasImage(data.project.images[0])
        if (canvasEditorRef.current) {
          canvasEditorRef.current.setMainImage(data.project.images[0])
          setCanvasHasContent(true)
        }
      }
      
      if (data.messages && data.messages.length > 0) {
        const restoredMessages = [
          messages[0],
          ...data.messages.map((msg, i) => ({
            id: i + 2, role: msg.role, content: msg.content, images: msg.images || []
          }))
        ]
        setMessages(restoredMessages)
      }
    } catch (error) {
      console.error('Load project error:', error)
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // Track scroll position to show/hide scroll-to-bottom button
  const handleChatScroll = useCallback(() => {
    const el = chatScrollRef.current
    if (!el) return
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120
    setShowScrollBtn(!nearBottom)
  }, [])

  useEffect(() => { scrollToBottom() }, [messages])

  // Mobile keyboard handling — when the mobile keyboard opens, the viewport
  // shrinks. We detect this via visualViewport and re-scroll the chat so the
  // latest message stays visible instead of being hidden behind the keyboard.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return
    const vv = window.visualViewport
    const onResize = () => {
      // Scroll chat to bottom when keyboard opens/closes
      setTimeout(() => scrollToBottom(), 100)
      // Also update a CSS variable so fixed footers can offset themselves
      const keyboardHeight = Math.max(0, window.innerHeight - vv.height)
      document.documentElement.style.setProperty('--keyboard-height', `${keyboardHeight}px`)
    }
    vv.addEventListener('resize', onResize)
    vv.addEventListener('scroll', onResize)
    return () => {
      vv.removeEventListener('resize', onResize)
      vv.removeEventListener('scroll', onResize)
      document.documentElement.style.removeProperty('--keyboard-height')
    }
  }, [])

  // ==========================================================================
  // VOICE INPUT (V2.0) — Web Speech API
  // ==========================================================================
  const toggleVoiceInput = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) { alert('Voice input is not supported in this browser. Try Chrome or Edge.'); return }

    if (isListening) {
      recognitionRef.current?.stop()
      setIsListening(false)
      return
    }

    const recognition = new SpeechRecognition()
    recognition.continuous = false
    recognition.interimResults = true
    recognition.lang = 'en-US'

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results).map(r => r[0].transcript).join('')
      setInput(prev => {
        // If we had partial results, replace; otherwise append
        if (event.results[0].isFinal) return prev ? prev + ' ' + transcript : transcript
        return transcript
      })
    }

    recognition.onend = () => { setIsListening(false); recognitionRef.current = null }
    recognition.onerror = (e) => { console.error('Speech error:', e.error); setIsListening(false) }

    recognition.start()
    recognitionRef.current = recognition
    setIsListening(true)
  }, [isListening])

  // ==========================================================================
  // QUICK ACTION EVENT LISTENER (V2.0)
  // ==========================================================================
  useEffect(() => {
    const handleQuickAction = (e) => {
      const { type, params } = e.detail
      switch (type) {
        case 'open_canvas':
          setMobileView('canvas')
          break
        case 'generate':
          if (params) { setInput(params); setTimeout(() => handleSend(), 100) }
          break
        case 'load_template':
          if (canvasEditorRef.current && params) {
            canvasEditorRef.current.loadTemplate({ name: params })
          }
          break
        case 'add_to_canvas':
          if (canvasEditorRef.current && params) {
            canvasEditorRef.current.addImage(params)
            setMobileView('canvas')
          }
          break
        default:
          console.log('Unknown quick action:', type, params)
      }
    }
    const handleOpenExport = () => setShowExportMenu(true)
    window.addEventListener('zenna-quick-action', handleQuickAction)
    window.addEventListener('zenna-open-export', handleOpenExport)
    return () => {
      window.removeEventListener('zenna-quick-action', handleQuickAction)
      window.removeEventListener('zenna-open-export', handleOpenExport)
    }
  }, [])

  useEffect(() => {
    if (canvasHasContent && window.innerWidth < 768) {
      setMobileView('canvas')
    }
  }, [canvasHasContent])

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showExportMenu && !e.target.closest('.export-dropdown')) {
        setShowExportMenu(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [showExportMenu])

  // Supported file types for upload
  const SUPPORTED_TYPES = {
    'image/jpeg': 'image', 'image/png': 'image', 'image/gif': 'image',
    'image/webp': 'image', 'image/svg+xml': 'image',
    'application/pdf': 'document',
    'text/plain': 'text', 'text/csv': 'text', 'text/markdown': 'text',
    'text/html': 'text', 'application/json': 'text',
    'application/xml': 'text', 'text/xml': 'text',
  }
  
  const EXT_TYPE_MAP = {
    '.txt': 'text', '.csv': 'text', '.md': 'text', '.json': 'text',
    '.xml': 'text', '.html': 'text', '.htm': 'text', '.log': 'text',
    '.rtf': 'text', '.svg': 'image', '.pdf': 'document',
    '.doc': 'text', '.docx': 'text',
    '.js': 'text', '.jsx': 'text', '.ts': 'text', '.tsx': 'text',
    '.css': 'text', '.py': 'text', '.rb': 'text', '.java': 'text',
    '.c': 'text', '.cpp': 'text', '.h': 'text', '.rs': 'text',
    '.go': 'text', '.php': 'text', '.sql': 'text', '.sh': 'text',
    '.yaml': 'text', '.yml': 'text', '.toml': 'text', '.ini': 'text',
    '.env': 'text', '.gitignore': 'text',
  }
  
  const getFileCategory = (file) => {
    if (SUPPORTED_TYPES[file.type]) return SUPPORTED_TYPES[file.type]
    const ext = '.' + file.name.split('.').pop().toLowerCase()
    return EXT_TYPE_MAP[ext] || null
  }

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })

  // Compress images to max dimension + JPEG quality to stay under API limits
  const compressImage = (dataUrl, maxDim = 1024, quality = 0.85) => new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      let { width, height } = img
      // Only resize if larger than maxDim
      if (width > maxDim || height > maxDim) {
        const ratio = Math.min(maxDim / width, maxDim / height)
        width = Math.round(width * ratio)
        height = Math.round(height * ratio)
      }
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, width, height)
      // Use JPEG for photos (smaller), PNG for transparent images
      const hasAlpha = dataUrl.includes('image/png')
      const outputType = hasAlpha ? 'image/png' : 'image/jpeg'
      const compressed = canvas.toDataURL(outputType, quality)
      resolve(compressed)
    }
    img.onerror = () => resolve(dataUrl) // fallback to original
    img.src = dataUrl
  })
  
  const readFileAsText = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(file)
  })

  // Handle file upload — supports multiple files, images, PDFs, text docs
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    
    const MAX_SIZE = 10 * 1024 * 1024
    const MAX_FILES = 5
    
    if (uploadedFiles.length + files.length > MAX_FILES) {
      alert(`You can upload up to ${MAX_FILES} files at a time. You have ${uploadedFiles.length} already.`)
      return
    }
    
    const newFiles = []
    
    for (const file of files) {
      if (file.size > MAX_SIZE) {
        const mb = (file.size / 1024 / 1024).toFixed(1)
        alert(
          `"${file.name}" is ${mb}MB — that's larger than the 10MB upload limit.\n\n` +
          `Try one of these:\n` +
          `  • Compress the image (most photo apps have an Export → Compressed option)\n` +
          `  • Save as JPG instead of PNG (often 5-10x smaller)\n` +
          `  • Resize to 4000px max dimension (anything larger gets downscaled server-side anyway)`
        )
        continue
      }
      
      const category = getFileCategory(file)
      
      if (!category) {
        alert(`"${file.name}" is not a supported file type. Supported: images, PDFs, text files, CSV, JSON, code files.`)
        continue
      }
      
      try {
        if (category === 'image') {
          const rawDataUrl = await readFileAsDataUrl(file)
          const dataUrl = await compressImage(rawDataUrl, 1024, 0.85)
          const base64 = dataUrl.split(',')[1]
          const mediaType = dataUrl.startsWith('data:image/png') ? 'image/png' : 'image/jpeg'
          newFiles.push({
            base64, mediaType, name: file.name,
            dataUrl, category: 'image'
          })
        } else if (category === 'document') {
          const dataUrl = await readFileAsDataUrl(file)
          const base64 = dataUrl.split(',')[1]
          newFiles.push({
            base64, mediaType: file.type || 'application/pdf', name: file.name,
            dataUrl: null, category: 'document',
            preview: `📄 ${file.name} (${(file.size / 1024).toFixed(0)}KB)`
          })
        } else if (category === 'text') {
          const textContent = await readFileAsText(file)
          newFiles.push({
            textContent, mediaType: file.type || 'text/plain', name: file.name,
            dataUrl: null, category: 'text',
            preview: `📝 ${file.name} (${textContent.length.toLocaleString()} chars)`
          })
        }
      } catch (err) {
        console.error(`Failed to read "${file.name}":`, err)
        alert(`Could not read "${file.name}". Try a different file.`)
      }
    }
    
    if (newFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...newFiles])
      inputRef.current?.focus()

      // Auto-load first uploaded image onto the canvas
      const firstImage = newFiles.find(f => f.category === 'image')
      if (firstImage && firstImage.dataUrl) {
        setCanvasImage(firstImage.dataUrl)
        setCanvasHasContent(true)

        // Load onto Fabric.js canvas with retry (ref may not be ready on fresh projects)
        const loadOntoCanvas = async () => {
          for (let attempt = 0; attempt < 10; attempt++) {
            if (canvasEditorRef.current?.setMainImage) {
              try {
                await canvasEditorRef.current.setMainImage(firstImage.dataUrl)
                console.log('Auto-loaded uploaded image onto canvas')
                pendingCanvasImage.current = null
                return
              } catch (err) {
                console.warn(`Canvas auto-load attempt ${attempt + 1} failed:`, err)
              }
            }
            await new Promise(r => setTimeout(r, 300))
          }
          // Canvas not ready yet — store for onCanvasReady to pick up
          console.log('Canvas not ready, storing pending image for onCanvasReady')
          pendingCanvasImage.current = firstImage.dataUrl
        }
        loadOntoCanvas()

        // Switch to 2D Canvas tab so they can see it
        if (viewMode !== '2d') setViewMode('2d')
        if (window.innerWidth < 768) setMobileView('canvas')
      }
    }
    
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const clearUpload = (index) => {
    if (index !== undefined) {
      setUploadedFiles(prev => prev.filter((_, i) => i !== index))
    } else {
      setUploadedFiles([])
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Handle tool actions from Zenna
  const handleToolActions = (toolActions) => {
    if (!toolActions || toolActions.length === 0) return
    
    toolActions.forEach(action => {
      console.log('Processing tool action:', action.type)
      
      // Track craft type from any generation action
      if (action.craftType) {
        setCurrentCraftType(action.craftType)
      }
      if (action.type === 'set_craft_type') {
        setCurrentCraftType(action.craftType)
        return // Nothing else to do
      }
      
      if (action.type === 'open_color_tools') {
        setColorToolsTab(action.tab || 'threads')
        if (action.colors) setColorToolsColors(action.colors)
        setShowColorTools(true)
      } else if (action.type === 'open_text_tool') {
        setTextToolSuggestions({ text: action.suggestedText, fontCategory: action.fontCategory, layout: action.layout })
        setShowTextTool(true)
      } else if (action.type === 'add_text_direct') {
        // V2.0: Place text directly on canvas without opening modal
        if (canvasEditorRef.current && action.text) {
          canvasEditorRef.current.addText(action.text, action.options || {})
          setMobileView('canvas') // Ensure canvas is visible
        }
      } else if (action.type === 'set_canvas_image') {
        // Load a composited image (text baked into pixels) onto the canvas
        if (action.url && canvasEditorRef.current) {
          canvasEditorRef.current.setMainImage(action.url).catch(() => {})
          setCanvasImage(action.url)
          setCanvasHasContent(true)
          setMobileView('canvas')
          console.log('Canvas updated with composited image:', action.url)
        }
      } else if (action.type === 'pattern_pdf_ready') {
        console.log('Pattern PDF data ready:', action.data)
        setPatternPdfData(action.data)
        setShowPatternPdfModal(true)
      } else if (action.type === 'cost_estimate') {
        console.log('Cost estimate data ready:', action.data)
        setCostEstimateData(action.data)
        setShowCostEstimate(true)
      } else if (action.type === 'social_post') {
        console.log('Social post data ready:', action.data)
        setSocialPostData(action.data)
        setShowSocialPost(true)
      } else if (action.type === 'open_3d_viewer') {
        console.log('Opening 3D viewer:', action.mode)
        setThreeDMode(action.mode || 'viewer')
        setViewMode('3d')
        if (window.innerWidth < 768) setMobileView('canvas')
      } else if (action.type === 'open_embroidery_editor') {
        console.log('Opening embroidery editor:', action)
        if (action.previewData) {
          setEmbroideryEditor({
            previewData: action.previewData,
            pesBase64: action.pesBase64 || null,
            filename: action.filename || 'zenna-embroidery.pes',
            imageUrl: canvasImage
          })
        }
        setStitchPreview(null)
        setViewMode('embroidery')
        if (window.innerWidth < 768) setMobileView('canvas')
      } else if (action.type === 'embroidery_text_result') {
        console.log('Embroidery text generated:', action)
        if (action.previewData) {
          setStitchPreview({
            previewData: action.previewData,
            pesBase64: action.pesBase64,
            filename: action.filename || 'zenna-text.pes'
          })
          if (window.innerWidth < 768) setMobileView('canvas')
        }
      } else if (action.type === 'edit_embroidery_region') {
        // Visual feedback loop: AI identified a region in the user's
        // screenshot and is asking the editor to apply a targeted edit.
        // Match by color (within ~30 BGR) and apply the action.
        if (!embroideryEditor?.previewData?.shapes) {
          console.warn('[edit_region] no embroidery editor open — ignoring')
          return
        }
        const tgt = (action.target_color_hex || '').replace('#', '').toLowerCase()
        const tgtRgb = tgt.length === 6 ? [
          parseInt(tgt.slice(0, 2), 16),
          parseInt(tgt.slice(2, 4), 16),
          parseInt(tgt.slice(4, 6), 16),
        ] : null
        const colorDist = (a, b) =>
          Math.sqrt((a[0]-b[0])**2 + (a[1]-b[1])**2 + (a[2]-b[2])**2)
        // If a bbox is provided, compute its world-space rectangle from the
        // design bounds. Bbox is normalized 0-1; convert to mm coords.
        let worldBbox = null
        const b = action.target_bbox
        const bounds = embroideryEditor.previewData.bounds
        if (b && bounds &&
            typeof b.x0 === 'number' && typeof b.y0 === 'number' &&
            typeof b.x1 === 'number' && typeof b.y1 === 'number') {
          const dw = bounds.maxX - bounds.minX
          const dh = bounds.maxY - bounds.minY
          worldBbox = {
            x0: bounds.minX + b.x0 * dw,
            y0: bounds.minY + b.y0 * dh,
            x1: bounds.minX + b.x1 * dw,
            y1: bounds.minY + b.y1 * dh,
          }
        }
        const shapeIntersectsBbox = (s, bb) => {
          if (!bb) return true
          // Test if any stitch point of the shape falls inside the bbox.
          // Cheaper than a true geometric intersection — and accurate
          // enough since stitches are dense.
          for (const seg of s.segments || []) {
            for (const pt of seg.points || []) {
              if (pt[0] >= bb.x0 && pt[0] <= bb.x1 &&
                  pt[1] >= bb.y0 && pt[1] <= bb.y1) return true
            }
          }
          return false
        }
        const matches = embroideryEditor.previewData.shapes.filter(s => {
          const sc = (s.color || '').replace('#', '').toLowerCase()
          if (sc.length !== 6 || !tgtRgb) return false
          const sRgb = [
            parseInt(sc.slice(0, 2), 16),
            parseInt(sc.slice(2, 4), 16),
            parseInt(sc.slice(4, 6), 16),
          ]
          if (colorDist(sRgb, tgtRgb) >= 50) return false
          // If user marked a specific region, only match shapes inside it.
          return shapeIntersectsBbox(s, worldBbox)
        })
        if (matches.length === 0) {
          const hint = worldBbox
            ? `(no ${action.target_color_hex} shapes inside the highlighted region)`
            : `(no shapes matched ${action.target_color_hex})`
          console.warn('[edit_region] no shapes matched target', hint, action.target_description || '')
          return
        }
        if (action.target_description) {
          console.log(`[edit_region] target: "${action.target_description}" — matched ${matches.length} shape(s)`)
        }
        // Apply the edit by mutating previewData.shapes (the editor reads from this)
        const newShapes = embroideryEditor.previewData.shapes.map(s => {
          if (!matches.includes(s)) return s
          if (action.action === 'recolor' && action.new_color_hex) {
            return { ...s, color: action.new_color_hex }
          }
          if (action.action === 'remove_color' || action.action === 'erase_shapes') {
            return null  // mark for removal
          }
          if (action.action === 'change_stitch_type' && action.new_stitch_type) {
            return { ...s, stitchType: action.new_stitch_type }
          }
          if (action.action === 'restitch') {
            return {
              ...s,
              stitchType: action.new_stitch_type || s.stitchType || 'fill',
              density: action.new_density || 1.0,
            }
          }
          return s
        }).filter(Boolean)
        setEmbroideryEditor(prev => ({
          ...prev,
          previewData: { ...prev.previewData, shapes: newShapes },
        }))
        console.log(`[edit_region] applied ${action.action} to ${matches.length} shape(s)`)
      } else if (action.type === 'embroidery_cleanup_review') {
        // Chat-triggered cleanup-review: open the same modal the export
        // button uses. The modal handles user confirmation and then calls
        // /to-embroidery-preview to actually generate stitches.
        console.log('Opening embroidery cleanup review:', action.detectedColors?.length, 'colors')
        setStitchPreview(null)
        setEmbroideryEditor(null)
        setCleanupReview({
          cleanedImageBase64: action.cleanedImageBase64,
          originalImageUrl: action.originalImageUrl || canvasImage,
          cropBbox: action.cropBbox,
          cleanedSize: action.cleanedSize,
          detectedColors: action.detectedColors,
          detectedTextRegions: action.detectedTextRegions || [],
          isHandPattern: action.isHandPattern || false,
          handPatternReason: action.handPatternReason || '',
          decisions: action.decisions,
          exportFormat: 'pes',
          exportSettings: null,
          isProcessing: false,
        })
      }
    })
  }

  // Handle image click from chat → load onto Fabric.js canvas
  // Copy assistant message text to clipboard (strips markdown formatting)
  const copyMessageText = async (content, messageId) => {
    try {
      // Strip markdown formatting for clean plaintext
      const plainText = content
        .replace(/\*\*(.*?)\*\*/g, '$1')       // bold
        .replace(/\*(.*?)\*/g, '$1')           // italic
        .replace(/#{1,6}\s?(.*?)$/gm, '$1')   // headers
        .replace(/`{3}[\s\S]*?`{3}/g, '')     // code blocks
        .replace(/`(.*?)`/g, '$1')             // inline code
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)') // links
        .replace(/^\s*[-*+]\s/gm, '• ')       // bullet points
        .replace(/\[ACTION:.*?\]/g, '')        // quick action buttons
        .trim()
      
      await navigator.clipboard.writeText(plainText)
      setCopiedMessageId(messageId)
      setTimeout(() => setCopiedMessageId(null), 2000)
    } catch (err) {
      console.error('Copy failed:', err)
    }
  }

  const handleImageClick = async (imageUrl, index, messageId) => {
    setCanvasImage(imageUrl)
    setSelectedImageIndex(index)
    setCanvasHasContent(true)
    
    // Wait for image to fully load onto Fabric.js canvas before saving
    if (canvasEditorRef.current) {
      try {
        await canvasEditorRef.current.setMainImage(imageUrl)
      } catch (e) {
        console.warn('Image load failed:', e)
      }
    }
    
    // Save AFTER canvas has the correct image (prevents stale JSON)
    if (projectId) {
      autoSaveCanvas(imageUrl, index)
    }
    if (window.innerWidth < 768) {
      setMobileView('canvas')
    }
  }

  // =========================================================================
  // TEMPLATE SELECTION HANDLER — V1.2 multi-type routing
  // Routes product/pattern/seasonal/saved template data to canvas editor
  // =========================================================================
  const handleTemplateSelect = useCallback((templateData) => {
    if (!canvasEditorRef.current) return
    const editor = canvasEditorRef.current

    switch (templateData.type) {
      // ── Product Template: set canvas size + safe area guide overlay ──
      case 'product': {
        const { width, height, safeArea, name, printArea } = templateData
        // Scale down for on-screen editing (most templates are 1200-10800px)
        const maxCanvasDim = 800
        const scale = Math.min(maxCanvasDim / width, maxCanvasDim / height, 1)
        const scaledW = Math.round(width * scale)
        const scaledH = Math.round(height * scale)

        editor.loadTemplate({
          width: scaledW,
          height: scaledH,
          backgroundColor: '#ffffff',
          name: `${name} (${printArea})`,
          guides: [{
            x: Math.round(safeArea.x * scale),
            y: Math.round(safeArea.y * scale),
            width: Math.round(safeArea.w * scale),
            height: Math.round(safeArea.h * scale),
            color: '#06b6d4',
          }],
        })
        // Store original dimensions for export at full resolution
        setCanvasSize({ width, height })
        break
      }

      // ── Pattern: load SVG pattern as centered canvas element ──
      case 'pattern': {
        const { name, svg, colors } = templateData
        editor.loadTemplate({
          backgroundColor: colors?.[1] || '#ffffff',
          name: `Pattern: ${name}`,
        })
        if (svg) {
          const coloredSvg = svg.replace(/currentColor/g, colors?.[0] || '#8B5CF6')
          const blob = new Blob([coloredSvg], { type: 'image/svg+xml' })
          const url = URL.createObjectURL(blob)
          editor.addImage(url).then(() => URL.revokeObjectURL(url)).catch(() => URL.revokeObjectURL(url))
        }
        break
      }

      // ── Seasonal: set up canvas with holiday color scheme + prompt via chat ──
      case 'seasonal': {
        const { name, colorScheme, holiday, designType } = templateData
        editor.loadTemplate({
          backgroundColor: colorScheme?.[2] || '#ffffff',
          name: `${holiday}: ${name}`,
        })
        const prompt = `Create a ${designType || 'design'} for ${holiday}: "${name}". Use these colors: ${(colorScheme || []).join(', ')}. Make it craft-ready for printing or cutting.`
        setInput(prompt)
        inputRef.current?.focus()
        break
      }

      // ── Saved Template: restore full Fabric.js canvas from JSON ──
      case 'saved': {
        const { name, canvasData } = templateData
        if (canvasData) {
          editor.loadFromJSON(canvasData)
        }
        if (name && (projectName === '' || projectName === 'Untitled Project')) {
          setProjectName(name)
        }
        break
      }

      // ── Fallback: pass through to generic loadTemplate ──
      default:
        editor.loadTemplate(templateData)
    }

    setCanvasHasContent(true)
    setShowTemplateLibrary(false)
    if (window.innerWidth < 768) {
      setMobileView('canvas')
    }
  }, [projectName])

  // Edit a user message: populate input with its text, remove that message and everything after it.
  // User can then edit the input and resend normally.
  const handleEditMessage = (messageId) => {
    const idx = messages.findIndex(m => m.id === messageId)
    if (idx === -1) return
    const message = messages[idx]
    if (message.role !== 'user') return
    setInput(message.content || '')
    // Keep everything BEFORE this message; drop this message and everything after
    setMessages(prev => prev.slice(0, idx))
    // Focus the input
    setTimeout(() => {
      const el = document.querySelector('textarea[placeholder*="design idea"]') || document.querySelector('textarea')
      if (el) {
        el.focus()
        el.setSelectionRange(el.value.length, el.value.length)
      }
    }, 50)
  }

  const handleSend = async () => {
    if ((!input.trim() && uploadedFiles.length === 0) || isLoading) return

    const imageFiles = uploadedFiles.filter(f => f.category === 'image')
    const fileLabel = uploadedFiles.length > 0
      ? `(Uploaded ${uploadedFiles.length} file${uploadedFiles.length > 1 ? 's' : ''}: ${uploadedFiles.map(f => f.name).join(', ')})`
      : ''
    
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: input.trim() || (uploadedFiles.length > 0 ? fileLabel : ''),
      images: imageFiles.map(f => ({ url: f.dataUrl, isUpload: true }))
    }

    setMessages(prev => [...prev, userMessage])
    const currentInput = input.trim()
    const currentUploadedFiles = [...uploadedFiles]
    setInput('')
    clearUpload()
    setIsLoading(true)
    setSelectedImageIndex(null)

    // Create abort controller for this request
    const controller = new AbortController()
    abortControllerRef.current = controller

    // Auto-save user message
    const savedProjectId = await autoSaveMessage(userMessage, projectId)

    try {
      const conversationHistory = messages
        .filter(msg => msg.id !== 1)
        .map(msg => ({
          role: msg.role,
          content: msg.content,
          // Don't re-send full base64 images in history — only note that images existed
          images: (msg.images || []).map(img => 
            img.url?.startsWith('data:') 
              ? { ...img, url: '[image previously sent]', isUpload: img.isUpload }
              : img  // keep remote URLs (Replicate-generated), strip base64
          )
        }))

      // Build canvas image before fetch — compress to stay under 1.5MB
      let canvasImageForSend = null
      if (canvasHasContent) {
        try {
          const fabricCanvas = canvasEditorRef.current?.fabricCanvas
          let rawDataUrl = fabricCanvas
            ? fabricCanvas.toDataURL({ format: 'jpeg', quality: 0.6, multiplier: 0.5 })
            : (canvasEditorRef.current?.getCanvasDataUrl() || canvasImage || null)
          if (rawDataUrl) {
            const sizeMB = (rawDataUrl.length * 0.75) / (1024 * 1024)
            if (sizeMB > 1.5) {
              // Still too large — compress more aggressively
              rawDataUrl = await compressImage(rawDataUrl, 800, 0.5)
              const newSizeMB = (rawDataUrl.length * 0.75) / (1024 * 1024)
              console.log(`Canvas compressed: ${sizeMB.toFixed(1)}MB → ${newSizeMB.toFixed(1)}MB`)
            }
            canvasImageForSend = rawDataUrl
          }
        } catch { canvasImageForSend = canvasImage || null }
      }

      const response = await fetch(`${API_URL}/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          message: currentInput || 'What can you tell me about this image?',
          conversationHistory,
          userEmail: user?.email || '',
          userName: user?.user_metadata?.full_name || user?.email?.split('@')[0] || '',
          uploadedFiles: currentUploadedFiles,
          // Legacy compatibility: send first image as uploadedImage too
          uploadedImage: currentUploadedFiles.find(f => f.category === 'image') || null,
          // Canvas image — pre-compressed above to stay under 1.5MB
          canvasImage: canvasImageForSend,
          pastHistory,
          projectId: savedProjectId || projectId
        })
      })

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}))
        throw new Error(errData.error || `Server error (${response.status})`)
      }

      const data = await response.json()

      const zennaResponse = {
        id: Date.now() + 1,
        role: 'assistant',
        content: data.message,
        images: data.images || []
      }
      setMessages(prev => [...prev, zennaResponse])

      // Handle any tool actions Zenna triggered
      if (data.toolActions && data.toolActions.length > 0) {
        handleToolActions(data.toolActions)
      }

      // Store gallery post ID for sharing
      if (data.galleryPostId) {
        setLastGalleryPostId(data.galleryPostId)
      }

      // Auto-save Zenna's response
      await autoSaveMessage(zennaResponse, savedProjectId)

      // Auto-load first generated image onto Fabric.js canvas
      if (data.images && data.images.length > 0) {
        const imgUrl = data.images[0].url
        setCanvasImage(imgUrl)
        setSelectedImageIndex(0)
        setCanvasHasContent(true)
        
        if (canvasEditorRef.current) {
          try {
            await canvasEditorRef.current.setMainImage(imgUrl)
          } catch (e) {
            console.warn('Auto-load image failed:', e)
          }
        }
        
        if (savedProjectId) {
          autoSaveCanvas(imgUrl, 0)
        }
      }
    } catch (error) {
      // Don't show error if user intentionally cancelled
      if (error.name === 'AbortError') {
        const stoppedResponse = {
          id: Date.now() + 1,
          role: 'assistant',
          content: "✂️ Got it — I stopped working on that. What would you like to do instead?",
          images: []
        }
        setMessages(prev => [...prev, stoppedResponse])
      } else {
        console.error('Chat error:', error)
        const errorResponse = {
          id: Date.now() + 1,
          role: 'assistant',
          content: error.message && error.message !== 'Failed to fetch'
            ? error.message
            : "Oops! I dropped a stitch there. 🧶 Something unexpected happened — check your internet connection and try sending that again!",
          images: []
        }
        setMessages(prev => [...prev, errorResponse])
      }
    } finally {
      setIsLoading(false)
      abortControllerRef.current = null
    }
  }

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleRename = async () => {
    if (!projectId || !projectName.trim() || !user?.email) return
    
    try {
      await fetch(`${API_URL}/chat/project/${projectId}/name`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, name: projectName.trim() })
      })
      setShowRenameModal(false)
    } catch (error) {
      console.error('Rename error:', error)
    }
  }

  // Export functions — upgraded to use Fabric.js canvas editor
  // Embroidery formats show a settings dialog first
  const handleExportClick = (format) => {
    if (['pes', 'dst', 'jef', 'exp'].includes(format)) {
      // Show embroidery settings dialog before exporting
      setPendingEmbroideryFormat(format)
      setPendingEmbroiderySettings({ maxColors: 64, threadColor: null, fabric: 'cotton' })
      setShowEmbroideryDialog(true)
      setShowExportMenu(false)
    } else {
      handleExport(format)
    }
  }

  const confirmEmbroideryExport = () => {
    setShowEmbroideryDialog(false)
    if (pendingEmbroideryFormat) {
      handleExport(pendingEmbroideryFormat)
    }
  }

  const handleExport = async (format) => {
    if (!canvasHasContent && !canvasImage) return
    setIsExporting(true)
    setShowExportMenu(false)
    
    try {
      // ==== PDF Pattern Export (client-side via PatternPdfModal) ====
      if (format === 'pdf') {
        // Grid-based crafts get stitch chart PDFs
        const gridCrafts = ['cross-stitch', 'knitting', 'crochet-chart', 'perler-beads', 'plastic-canvas', 'latch-hook', 'diamond-painting', 'mosaic', 'pixel-art', 'beading', 'needlepoint']
        const craftType = currentCraftType || 'cross-stitch'
        const isGridCraft = gridCrafts.includes(craftType)
        
        if (isGridCraft) {
          const defaultPatternData = {
            type: craftType,
            title: projectName || 'Zenna Pattern',
            dimensions: { width: 80, height: 80, totalCells: 6400 },
            finishedSize: 'Varies based on fabric count',
            difficulty: 'intermediate',
            estimatedTime: '8-16 hours',
            symbolKey: [],
            colorCount: 12,
            materials: [],
            instructions: '',
            generatedAt: new Date().toISOString(),
            generator: 'Zenna - Your Ultimate Craft Companion'
          }
          setPatternPdfData(defaultPatternData)
          setShowPatternPdfModal(true)
          setIsExporting(false)
          return
        } else {
          // Non-grid crafts: export design as print-ready PDF image
          const dataUrl = canvasEditorRef.current?.getCanvasDataUrl() || canvasImage
          if (!dataUrl) { setIsExporting(false); return }
          // Create a simple print PDF with the design image
          const printWindow = window.open('', '_blank')
          if (printWindow) {
            printWindow.document.write(`
              <!DOCTYPE html><html><head><title>${projectName || 'Zenna Design'} - ${craftType}</title>
              <style>@page{margin:0.5in}body{margin:0;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;font-family:Arial,sans-serif}
              img{max-width:100%;max-height:85vh;object-fit:contain}
              .info{margin-top:16px;text-align:center;color:#666;font-size:12px}</style></head>
              <body><img src="${dataUrl}" /><div class="info"><strong>${projectName || 'Zenna Design'}</strong><br>${craftType.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} Design<br>Created with Zenna - Your Ultimate Craft Companion</div>
              <script>window.onload=()=>{window.print()}</script></body></html>
            `)
            printWindow.document.close()
          }
          setIsExporting(false)
          return
        }
      }

      // Helper: get canvas data URL from Fabric.js or fallback
      const getExportDataUrl = () => {
        if (canvasEditorRef.current) {
          return canvasEditorRef.current.getCanvasDataUrl()
        }
        return canvasImage
      }

      // ==== PNG Direct Download (from Fabric.js canvas — 2x DPI) ====
      if (format === 'png') {
        const dataUrl = getExportDataUrl()
        if (!dataUrl) return

        if (dataUrl.startsWith('data:')) {
          const link = document.createElement('a')
          link.href = dataUrl
          link.download = `${projectName || 'zenna-design'}-${Date.now()}.png`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        } else {
          const response = await fetch(dataUrl)
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `${projectName || 'zenna-design'}-${Date.now()}.png`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        }
      }
      
      // ==== SVG Export — server-side potrace for cut-ready vector paths ====
      // Fabric.js toSVG() only wraps raster images in <image> tags — NOT cuttable!
      // Potrace traces actual <path> elements that Cricut/Silhouette can cut.
      else if (format === 'svg') {
        const imageUrl = getExportDataUrl() || canvasImage
        if (!imageUrl) { setIsExporting(false); return }
        
        const response = await fetch(`${API_URL}/export/to-svg`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageUrl })
        })
        if (!response.ok) {
          const error = await response.json()
          throw new Error(error.error || 'SVG export failed')
        }
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${projectName || 'zenna-design'}-${Date.now()}.svg`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
      
      // ==== xTool / LightBurn SVG (laser cut/score/engrave) ====
      // Two-step: clean image first (vtracer-quality vector paths), then
      // emit color-grouped SVG with mm dimensions. xCS will import at
      // physical size; LightBurn auto-assigns each color group to a layer.
      else if (format === 'xtool-svg') {
        const imageUrl = getExportDataUrl() || canvasImage
        if (!imageUrl) { setIsExporting(false); return }

        const cleanResp = await fetch(`${API_URL}/export/analyze-and-clean`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrl,
            widthMm: 100,
            contentType: 'logo',
            simplifyLevel: 'light',
          }),
        })
        if (!cleanResp.ok) {
          const error = await cleanResp.json()
          throw new Error(error.error || 'Cleanup failed')
        }
        const cleanData = await cleanResp.json()

        const svgResp = await fetch(`${API_URL}/export/to-xtool-svg`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cleanedImageBase64: cleanData.cleanedImageBase64,
            widthMm: 100,
          }),
        })
        if (!svgResp.ok) {
          const error = await svgResp.json()
          throw new Error(error.error || 'SVG export failed')
        }
        const svgData = await svgResp.json()

        const blob = new Blob([svgData.svg], { type: 'image/svg+xml' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = svgData.filename || `${projectName || 'zenna-design'}-xtool-${Date.now()}.svg`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }

      // ==== 3D Engrave depth map — luminance OR AI (Depth Anything) ====
      // Outputs a grayscale PNG where DARK = DEEP. Drop into xCS '3D Curve'
      // mode to carve relief into wood/acrylic/slate.
      else if (format === 'depth-luminance' || format === 'depth-ai') {
        const imageUrl = getExportDataUrl() || canvasImage
        if (!imageUrl) { setIsExporting(false); return }
        const mode = format === 'depth-ai' ? 'ai' : 'luminance'

        const resp = await fetch(`${API_URL}/export/to-depth-map`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrl,
            mode,
            invert: false,    // dark stays dark = deep (xCS default)
            blurPx: 3,        // smooth pixel-step terraces
            floor: 30,        // deepest engrave 88% (saves thin stock from burning through)
          }),
        })
        if (!resp.ok) {
          const error = await resp.json()
          throw new Error(error.error || '3D depth export failed')
        }
        const data = await resp.json()

        // depthMapBase64 is a data URL ready for download
        const link = document.createElement('a')
        link.href = data.depthMapBase64
        link.download = data.filename || `${projectName || 'zenna-design'}-3d-engrave-${Date.now()}.png`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }

      // ==== DXF Vector Conversion (server-side) ====
      else if (format === 'dxf') {
        const imageUrl = getExportDataUrl() || canvasImage
        const response = await fetch(`${API_URL}/export/to-dxf`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageUrl })
        })
        if (!response.ok) {
          const error = await response.json()
          throw new Error(error.error || 'Export failed')
        }
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${projectName || 'zenna-design'}-${Date.now()}.dxf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
      
      // ==== Embroidery Formats (PES, DST, JEF, EXP) — cleanup-review then digitize ====
      else if (['pes', 'dst', 'jef', 'exp'].includes(format)) {
        const imageUrl = getExportDataUrl() || canvasImage
        if (!imageUrl) { setIsExporting(false); return }

        // Step 1: run the cleanup analysis. This strips background, smart-
        // crops, consolidates colors. The user gets to confirm/adjust the
        // result before stitches are generated.
        const cleanupResp = await fetch(`${API_URL}/export/analyze-and-clean`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrl,
            widthMm: 100,
            contentType: 'logo',
          }),
        })
        if (!cleanupResp.ok) {
          const error = await cleanupResp.json()
          throw new Error(error.error || 'Cleanup analysis failed')
        }
        const cleanupData = await cleanupResp.json()
        if (!cleanupData.success) {
          throw new Error(cleanupData.error || 'Cleanup analysis returned no design')
        }

        // Step 2: open the review modal. Stitch generation runs after the
        // user confirms (handled in the modal's onConfirm callback below).
        setCleanupReview({
          cleanedImageBase64: cleanupData.cleanedImageBase64,
          originalImageUrl: imageUrl,
          cropBbox: cleanupData.cropBbox,
          cleanedSize: cleanupData.cleanedSize,
          detectedColors: cleanupData.detectedColors,
          detectedTextRegions: cleanupData.detectedTextRegions || [],
          decisions: cleanupData.decisions,
          exportFormat: format,
          exportSettings: pendingEmbroiderySettings,
          isProcessing: false,
        })
        // Don't reset pending settings yet — they're used after confirmation
      }
    } catch (error) {
      console.error('Export failed:', error)
      alert('Export failed: ' + error.message)
    } finally {
      setIsExporting(false)
    }
  }

  // =========================================================================
  // CLEANUP REVIEW: user confirmed — now run stitch generation on the
  // approved cleaned image (which includes any chip-removals + paint edits).
  // =========================================================================
  const handleCleanupConfirm = useCallback(async (finalImageBase64, finalColors, fabricKey, textFontOverrides) => {
    if (!cleanupReview) return
    setCleanupReview(prev => prev ? { ...prev, isProcessing: true } : prev)
    try {
      const requestedColors = cleanupReview.exportSettings?.maxColors ?? 64
      const threadCol       = cleanupReview.exportSettings?.threadColor ?? null
      // Modal's fabric pick wins over any pre-set; falls back to cotton
      const fabricType      = fabricKey || cleanupReview.exportSettings?.fabric || 'cotton'

      // Hybrid AI digitization (try first): the heuristic digitizer
      // enumerates components from the cleanup image, Claude looks at the
      // annotated overview and only chooses the stitch type per
      // component, then the heuristic re-runs with those labels. AI
      // never invents geometry — it only labels real components. Falls
      // back to the heuristic-only path on any error.
      let data = null
      try {
        const aiResp = await fetch(`${API_URL}/export/ai-classify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cleanedImageBase64: finalImageBase64,
            format: cleanupReview.exportFormat,
            maxWidth: 100,
            maxHeight: 100,
            stitchLength: 2.5,
            maxColors: requestedColors,
            threadColor: threadCol,
            fabric: fabricType,
            textFontOverrides: textFontOverrides || null,
          }),
        })
        if (aiResp.ok) {
          const aiData = await aiResp.json()
          if (aiData.success) data = aiData
        }
      } catch (e) {
        console.warn('ai-classify failed, falling back to heuristic:', e)
      }
      if (!data) {
        const response = await fetch(`${API_URL}/export/to-embroidery-preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrl: 'cleaned',
            cleanedImageBase64: finalImageBase64,
            format: cleanupReview.exportFormat,
            maxWidth: 100,
            maxHeight: 100,
            stitchLength: 2.5,
            maxColors: requestedColors,
            threadColor: threadCol,
            fabric: fabricType,
            textFontOverrides: textFontOverrides || null,
          }),
        })
        if (!response.ok) {
          const error = await response.json()
          throw new Error(error.error || 'Stitch generation failed')
        }
        data = await response.json()
        if (!data.success) throw new Error(data.error || 'Stitch generation failed')
      }
      // Open both: stitchPreview (mini view in any tab) AND the full editor.
      // Switch to the embroidery tab so the user lands directly on the
      // editor — matches what the chat AI was doing before the cleanup
      // step was inserted.
      const previewBundle = {
        previewData: data.preview?.stitchData || data.preview,
        pesBase64: data.file,
        filename: data.filename || 'zenna-embroidery.pes',
      }
      setStitchPreview(previewBundle)
      setEmbroideryEditor({
        ...previewBundle,
        imageUrl: cleanupReview.originalImageUrl,
      })
      setViewMode('embroidery')
      if (window.innerWidth < 768) setMobileView('canvas')
    } catch (err) {
      console.error('Stitch generation failed:', err)
      alert('Stitch generation failed: ' + err.message)
    } finally {
      setCleanupReview(null)
      setPendingEmbroiderySettings(null)
      setPendingEmbroideryFormat(null)
    }
  }, [cleanupReview])

  const handleCleanupCancel = useCallback(() => {
    setCleanupReview(null)
    setPendingEmbroiderySettings(null)
    setPendingEmbroideryFormat(null)
  }, [])

  // =========================================================================
  // GENERATE PINTEREST PIN — Composite canvas design into 1000x1500 pin image
  // =========================================================================
  const handleGeneratePin = async () => {
    setShowExportMenu(false)
    const imageUrl = canvasImage || canvasEditorRef.current?.getCanvasDataUrl()
    if (!imageUrl) return alert('No design to turn into a pin!')

    setPromptState({
      title: 'Generate Pinterest Pin',
      description: 'Create a 1000×1500 pin with your title and description.',
      fields: [
        { name: 'title', label: 'Pin Title', placeholder: 'My craft project', default: projectName || 'My craft project', maxLength: 100 },
        { name: 'description', label: 'Description', multiline: true, maxLength: 500, default: `Made with Zenna AI — ${currentCraftType || 'handmade craft'} pattern` }
      ],
      submitLabel: 'Generate Pin',
      onSubmit: async ({ title, description }) => {
        setPromptState(null)
        try {
          const response = await fetch(`${API_URL}/export/to-pin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              imageUrl,
              title: (title || '').slice(0, 100),
              description: (description || '').slice(0, 500),
              style: 'light',
              watermark: true
            })
          })
          if (!response.ok) throw new Error('Pin generation failed')
          const blob = await response.blob()
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `zenna-pin-${Date.now()}.png`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
        } catch (error) {
          console.error('Pin error:', error)
          alert('Failed to generate pin. Try again!')
        }
      }
    })
  }

  // =========================================================================
  // SHARE TO GALLERY — Post design to Zenna community gallery
  // =========================================================================
  const handleShareToGallery = async () => {
    setShowExportMenu(false)
    const imageUrl = canvasImage || canvasEditorRef.current?.getCanvasDataUrl()
    if (!imageUrl) return alert('No design to share!')

    setPromptState({
      title: 'Share to Zenna Gallery',
      description: 'Give your design a title so the community can find it.',
      fields: [
        { name: 'title', label: 'Title', placeholder: 'My Zenna Design', default: projectName || 'My Zenna Design', maxLength: 80 }
      ],
      submitLabel: 'Post to Gallery',
      onSubmit: async ({ title }) => {
        setPromptState(null)
        if (!title || !title.trim()) return
        try {
          const response = await fetch(`${API_URL}/community/gallery`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-user-email': user?.email
            },
            body: JSON.stringify({
              title: title.trim(),
              description: 'Created with Zenna AI',
              image_urls: [imageUrl],
              thumbnail_url: imageUrl,
              craft_type: currentCraftType || 'general',
              tags: currentCraftType ? [currentCraftType, 'ai-generated'] : ['ai-generated'],
              project_id: projectId || null
            })
          })

          if (response.ok) {
            const result = await response.json()
            if (result.post?.id) setLastGalleryPostId(result.post.id)
            alert('🎨 Your design has been posted to the Zenna Gallery!')
          } else {
            const err = await response.json().catch(() => ({}))
            alert('Failed to post: ' + (err.error || 'Please try again'))
          }
        } catch (error) {
          console.error('Gallery share failed:', error)
          alert('Share failed — please try again')
        }
      }
    })
  }

  // =========================================================================
  // SHARE TO SOCIAL — Copy link, Pinterest, Facebook, X, Instagram, TikTok
  // =========================================================================
  // =========================================================================
  // RESIZABLE PANEL DIVIDER — drag to resize canvas / chat split
  // =========================================================================
  const handlePanelResize = useCallback((e) => {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = chatPanelWidth

    const onMouseMove = (moveE) => {
      const delta = startX - moveE.clientX  // dragging left = wider chat
      const newWidth = Math.max(280, Math.min(700, startWidth + delta))
      setChatPanelWidth(newWidth)
    }

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [chatPanelWidth])

  // Helper: fetch an image URL and return a File for Web Share API / downloads
  const fetchImageAsFile = async (url, filename = 'zenna-design.png') => {
    // Handle data URLs directly
    if (url.startsWith('data:')) {
      const res = await fetch(url)
      const blob = await res.blob()
      return new File([blob], filename, { type: blob.type || 'image/png' })
    }
    // Remote URL — fetch through browser
    const res = await fetch(url)
    if (!res.ok) throw new Error(`Failed to fetch image: ${res.status}`)
    const blob = await res.blob()
    return new File([blob], filename, { type: blob.type || 'image/png' })
  }

  // Helper: trigger a browser download
  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  // Helper: true if device supports the Web Share API with files
  const canNativeShareFiles = () => {
    return typeof navigator.share === 'function' &&
           typeof navigator.canShare === 'function'
  }

  const handleShareSocial = async (platform) => {
    setShowExportMenu(false)
    const imageUrl = canvasImage || canvasEditorRef.current?.getCanvasDataUrl()
    if (!imageUrl) return alert('No design to share!')

    // Use actual gallery post URL if available, otherwise zennago.com
    const shareUrl = lastGalleryPostId
      ? `https://zennago.com/gallery/${lastGalleryPostId}`
      : 'https://zennago.com'
    const craftLabel = currentCraftType ? currentCraftType.replace(/-/g, ' ') + ' ' : ''
    const shareText = `Check out this ${craftLabel}design I made with Zenna AI! ✨ #crafting #diy #handmade #zennago`

    // ── NATIVE SHARE (mobile) ─────────────────────────────────────────────
    // Use navigator.share for the generic "native" button — opens iOS/Android
    // share sheet with the actual image file + caption. User picks Instagram,
    // TikTok, Messages, Snap, anything.
    if (platform === 'native') {
      try {
        if (!canNativeShareFiles()) {
          throw new Error('Web Share API not supported')
        }
        const file = await fetchImageAsFile(imageUrl, 'zenna-design.png')
        const shareData = {
          title: 'My Zenna Design',
          text: shareText,
          url: shareUrl,
          files: [file]
        }
        // Test if we can share files on this device
        if (navigator.canShare(shareData)) {
          await navigator.share(shareData)
        } else {
          // Fall back to text-only share
          await navigator.share({ title: 'My Zenna Design', text: shareText, url: shareUrl })
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Native share failed:', err)
          // Silent fallback to copy
          try {
            await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`)
            alert('Native share not available — link + caption copied to clipboard instead.')
          } catch {
            alert('Share failed. Try the Copy Link option instead.')
          }
        }
      }
      return
    }

    if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`)
        alert('🔗 Share link + caption copied!')
      } catch {
        const textArea = document.createElement('textarea')
        textArea.value = `${shareText}\n${shareUrl}`
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        alert('🔗 Share link + caption copied!')
      }
    } else if (platform === 'pinterest') {
      const pinUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&media=${encodeURIComponent(imageUrl)}&description=${encodeURIComponent(shareText)}`
      window.open(pinUrl, '_blank', 'width=750,height=550')
    } else if (platform === 'facebook') {
      const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`
      window.open(fbUrl, '_blank', 'width=600,height=400')
    } else if (platform === 'twitter') {
      const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`
      window.open(tweetUrl, '_blank', 'width=600,height=400')
    } else if (platform === 'instagram' || platform === 'tiktok') {
      // On MOBILE: use native share sheet (best path — drops them straight into IG/TikTok)
      if (canNativeShareFiles()) {
        try {
          const file = await fetchImageAsFile(imageUrl, 'zenna-design.png')
          const shareData = {
            title: 'My Zenna Design',
            text: shareText,
            files: [file]
          }
          if (navigator.canShare(shareData)) {
            await navigator.share(shareData)
            return
          }
        } catch (err) {
          if (err.name !== 'AbortError') console.warn('Native share fallback:', err)
        }
      }

      // On DESKTOP: download the image, copy the caption, show QR code so user
      // can open on their phone and upload from camera roll
      try {
        const file = await fetchImageAsFile(imageUrl, 'zenna-design.png')
        downloadBlob(file, 'zenna-design.png')
        await navigator.clipboard.writeText(shareText)
        // Show QR code modal with the image URL so user can open on phone
        setQrShareState({
          platform,
          imageUrl: imageUrl.startsWith('data:') ? shareUrl : imageUrl,
          caption: shareText,
          shareUrl
        })
      } catch {
        alert(`Copy this caption:\n\n${shareText}`)
      }
    }
  }

  return (
    <div className="h-screen flex flex-col bg-zenna-cream dark:bg-slate-900">
      {/* Global Stats Bar */}
      <ZennaStatsBar />

      {/* Embroidery cleanup review — modal that sits between upload and
          stitch generation. Lets the user confirm what we detected as bg/
          threads/noise before we commit to PES. */}
      {cleanupReview && (
        <EmbroideryCleanupReview
          cleanedImageBase64={cleanupReview.cleanedImageBase64}
          originalImageUrl={cleanupReview.originalImageUrl}
          cropBbox={cleanupReview.cropBbox}
          cleanedSize={cleanupReview.cleanedSize}
          detectedColors={cleanupReview.detectedColors}
          detectedTextRegions={cleanupReview.detectedTextRegions}
          decisions={cleanupReview.decisions}
          isHandPattern={cleanupReview.isHandPattern}
          handPatternReason={cleanupReview.handPatternReason}
          isProcessing={cleanupReview.isProcessing}
          onConfirm={handleCleanupConfirm}
          onCancel={handleCleanupCancel}
          currentSimplifyLevel={cleanupReview.simplifyLevel || 'light'}
          onResimplify={async (level) => {
            // Re-run /analyze-and-clean with the new simplify level.
            // Reuses the same source image so the re-cleanup is fast (~3s).
            if (!cleanupReview.originalImageUrl) {
              console.warn('[resimplify] no original image available')
              return
            }
            setCleanupReview(prev => prev ? { ...prev, isProcessing: true } : prev)
            try {
              const r = await fetch(`${API_URL}/export/analyze-and-clean`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  imageUrl: cleanupReview.originalImageUrl,
                  widthMm: 100,
                  contentType: 'logo',
                  simplifyLevel: level,
                }),
              })
              if (!r.ok) throw new Error(`status ${r.status}`)
              const data = await r.json()
              setCleanupReview(prev => prev ? {
                ...prev,
                cleanedImageBase64: data.cleanedImageBase64,
                cropBbox: data.cropBbox,
                cleanedSize: data.cleanedSize,
                detectedColors: data.detectedColors,
                detectedTextRegions: data.detectedTextRegions,
                isHandPattern: data.isHandPattern || false,
                handPatternReason: data.handPatternReason || '',
                decisions: data.decisions,
                simplifyLevel: level,
                isProcessing: false,
              } : prev)
            } catch (err) {
              console.error('[resimplify] failed:', err)
              setCleanupReview(prev => prev ? { ...prev, isProcessing: false } : prev)
              alert('Re-simplify failed: ' + (err.message || 'unknown error'))
            }
          }}
        />
      )}

      {/* Hidden file input — supports multiple files */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf,.txt,.csv,.md,.json,.xml,.html,.log,.rtf,.doc,.docx,.js,.jsx,.ts,.tsx,.css,.py,.rb,.java,.c,.cpp,.h,.rs,.go,.php,.sql,.sh,.yaml,.yml,.toml,.ini,.env"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Color Tools Panel */}
      <ColorTools
        isOpen={showColorTools}
        onClose={() => setShowColorTools(false)}
        designColors={designColors}
        initialTab={colorToolsTab}
        highlightColors={colorToolsColors}
        craftType={currentCraftType}
      />

      {/* Cost Estimate Panel */}
      <CostEstimatePanel
        isOpen={showCostEstimate}
        onClose={() => { setShowCostEstimate(false); setCostEstimateData(null) }}
        data={costEstimateData}
      />

      {/* Social Post Panel */}
      <SocialPostPanel
        isOpen={showSocialPost}
        onClose={() => { setShowSocialPost(false); setSocialPostData(null) }}
        data={socialPostData}
      />

      {/* Template Library Modal — V1.2 with full props */}
      {showTemplateLibrary && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowTemplateLibrary(false)}>
          <div 
            className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full shadow-2xl max-h-[85vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <TemplateLibrary
              onClose={() => setShowTemplateLibrary(false)}
              onSelectTemplate={handleTemplateSelect}
              canvasRef={canvasEditorRef}
              userEmail={user?.email}
            />
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-4">
              Rename Project
            </h3>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Enter project name..."
              className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-zenna-violet/50 mb-4"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleRename()}
            />
            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1" onClick={() => setShowRenameModal(false)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleRename} disabled={!projectName.trim()}>
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Text Tool Modal */}
      {showTextTool && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <TextTool
            initialText={textToolSuggestions?.text}
            initialFontCategory={textToolSuggestions?.fontCategory}
            initialLayout={textToolSuggestions?.layout}
            onTextGenerated={(textData) => {
              // Add text image as a new layer on the Fabric.js canvas
              if (canvasEditorRef.current) {
                canvasEditorRef.current.addImage(textData.url)
                setCanvasHasContent(true)
              }
              setShowTextTool(false)
              setTextToolSuggestions(null)
              
              const textMessage = {
                id: Date.now(),
                role: 'assistant',
                content: `✨ Created text design!\n\n**"${textData.text}"**\nFont: ${textData.font}\nSize: ${textData.fontSize}px\n\nThe text is now on your canvas as a new layer. You can move, resize, or layer it with other elements!`,
                images: [{ url: textData.url }]
              }
              setMessages(prev => [...prev, textMessage])
              
              if (projectId) {
                autoSaveMessage(textMessage, projectId)
              }
            }}
            onClose={() => {
              setShowTextTool(false)
              setTextToolSuggestions(null)
            }}
          />
        </div>
      )}

      {/* QR share bridge for desktop Instagram/TikTok */}
      {qrShareState && (
        <QrShareModal
          platform={qrShareState.platform}
          imageUrl={qrShareState.imageUrl}
          caption={qrShareState.caption}
          shareUrl={qrShareState.shareUrl}
          onClose={() => setQrShareState(null)}
        />
      )}

      {/* Mobile-friendly prompt modal (replaces window.prompt) */}
      {promptState && (
        <PromptModal
          open={true}
          title={promptState.title}
          description={promptState.description}
          fields={promptState.fields}
          submitLabel={promptState.submitLabel}
          onSubmit={promptState.onSubmit}
          onCancel={() => setPromptState(null)}
        />
      )}

      {/* Version History Panel */}
      {showVersionHistory && projectId && (
        <VersionHistoryPanel
          projectId={projectId}
          userEmail={user?.email || ''}
          onClose={() => setShowVersionHistory(false)}
          onRestore={() => {
            // Reload the project to show restored state
            loadProject(projectId)
          }}
        />
      )}

      {/* Pattern PDF Modal */}
      <PatternPdfModal
        isOpen={showPatternPdfModal}
        onClose={() => { setShowPatternPdfModal(false); setPatternPdfData(null) }}
        patternData={patternPdfData}
        canvasImage={canvasImage || canvasEditorRef.current?.getCanvasDataUrl()}
        craftType={currentCraftType}
        projectName={projectName}
      />

      {/* Craft Converter Modal — pixelate canvas into grid + craft-specific palette */}
      <CraftConverter
        isOpen={showCraftConverter}
        onClose={() => setShowCraftConverter(false)}
        canvasImage={canvasImage || canvasEditorRef.current?.getCanvasDataUrl()}
        craftType={currentCraftType}
        onApplyToCanvas={(previewDataUrl) => {
          setCanvasImage(previewDataUrl)
          if (canvasEditorRef.current?.loadImage) {
            canvasEditorRef.current.loadImage(previewDataUrl)
          }
          setShowCraftConverter(false)
        }}
      />

      {/* Embroidery Export Settings Dialog */}
      {showEmbroideryDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowEmbroideryDialog(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-sm w-full shadow-2xl p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-1">🧵 Embroidery Settings</h3>
            <p className="text-xs text-slate-400 mb-4">Configure before exporting to {pendingEmbroideryFormat?.toUpperCase()}</p>
            
            {/* Color Mode */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Thread Colors</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setPendingEmbroiderySettings(prev => ({ ...prev, maxColors: 1, threadColor: prev?.threadColor || '#000000' }))}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                    pendingEmbroiderySettings?.maxColors === 1 
                      ? 'border-zenna-violet bg-zenna-violet/10 text-zenna-violet' 
                      : 'border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-slate-300'
                  }`}
                >
                  Single Color
                </button>
                <button
                  onClick={() => setPendingEmbroiderySettings(prev => ({ ...prev, maxColors: 64, threadColor: null }))}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                    pendingEmbroiderySettings?.maxColors !== 1 
                      ? 'border-zenna-violet bg-zenna-violet/10 text-zenna-violet' 
                      : 'border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-slate-300'
                  }`}
                >
                  Multi-Color (Auto)
                </button>
              </div>
            </div>

            {/* Thread Color Picker (only in single-color mode) */}
            {pendingEmbroiderySettings?.maxColors === 1 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Thread Color</label>
                <div className="flex gap-2 items-center">
                  <input 
                    type="color" 
                    value={pendingEmbroiderySettings?.threadColor || '#000000'}
                    onChange={(e) => setPendingEmbroiderySettings(prev => ({ ...prev, threadColor: e.target.value }))}
                    className="w-10 h-10 rounded-lg border-2 border-slate-200 dark:border-slate-600 cursor-pointer"
                  />
                  <div className="flex gap-1.5">
                    {['#000000', '#FFFFFF', '#FF0000', '#00008B', '#006400', '#8B4513'].map(c => (
                      <button 
                        key={c} 
                        onClick={() => setPendingEmbroiderySettings(prev => ({ ...prev, threadColor: c }))}
                        className={`w-7 h-7 rounded-full border-2 transition-all ${
                          pendingEmbroiderySettings?.threadColor === c ? 'border-zenna-violet scale-110' : 'border-slate-300 dark:border-slate-600'
                        }`}
                        style={{ backgroundColor: c }}
                        title={c}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Fabric Type */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Fabric Type</label>
              <select 
                value={pendingEmbroiderySettings?.fabric || 'cotton'}
                onChange={(e) => setPendingEmbroiderySettings(prev => ({ ...prev, fabric: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-slate-700 dark:text-slate-200"
              >
                <option value="cotton">Cotton (standard)</option>
                <option value="cotton-thick">Heavy cotton / Denim</option>
                <option value="polyester">Polyester</option>
                <option value="knit">Stretchy knit</option>
                <option value="fleece">Fleece / Plush</option>
                <option value="silk">Silk / Satin</option>
                <option value="leather">Leather / Vinyl</option>
                <option value="felt">Felt</option>
                <option value="towel">Towel / Terry cloth</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button 
                onClick={() => setShowEmbroideryDialog(false)}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button 
                onClick={confirmEmbroideryExport}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-gradient-zenna hover:opacity-90 transition-opacity"
              >
                Export {pendingEmbroideryFormat?.toUpperCase()}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="px-3 sm:px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-zenna flex items-center justify-center">
                <span className="text-white font-bold text-xs sm:text-sm">Z</span>
              </div>
              <button 
                onClick={() => projectId && setShowRenameModal(true)}
                className="flex flex-col hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg px-2 py-1 transition-colors"
              >
                <span className="font-display font-semibold text-slate-800 dark:text-white text-sm sm:text-base leading-tight">
                  {projectName || 'Workspace'}
                </span>
                <span className="text-[10px] text-slate-400 flex items-center gap-1">
                  {isAutoSaving ? (
                    <>
                      <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full animate-pulse"></span>
                      Saving...
                    </>
                  ) : projectId ? (
                    <>
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                      Auto-saved
                    </>
                  ) : (
                    'New project'
                  )}
                </span>
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            
            {/* Templates Button */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowTemplateLibrary(true)}
              className="text-xs sm:text-sm"
              title="Design Templates"
            >
              <svg className="w-4 h-4 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
              </svg>
              <span className="hidden sm:inline">Templates</span>
            </Button>
            
            {/* Color Tools Button */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowColorTools(true)}
              className="text-xs sm:text-sm"
              title="Color Tools"
            >
              <svg className="w-4 h-4 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
              <span className="hidden sm:inline">Colors</span>
            </Button>

            {/* Craft Converter Button */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowCraftConverter(true)}
              className="text-xs sm:text-sm"
              title={`Convert to ${currentCraftType || 'craft'} grid`}
            >
              <svg className="w-4 h-4 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16M8 4v16M12 4v16M16 4v16" />
              </svg>
              <span className="hidden sm:inline">Convert</span>
            </Button>
            
            {/* Text Tool Button */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowTextTool(true)}
              className="text-xs sm:text-sm"
              title="Add Text"
            >
              <svg className="w-4 h-4 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span className="hidden sm:inline">Text</span>
            </Button>
            
            {/* Version History */}
            {projectId && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setShowVersionHistory(true)}
                className="text-xs sm:text-sm"
                title="Version history"
              >
                <svg className="w-4 h-4 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="hidden sm:inline">History</span>
              </Button>
            )}

            {/* Export Dropdown */}
            <div className="relative export-dropdown">
              <Button
                size="sm"
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={(!canvasHasContent && !canvasImage) || isExporting}
                className="text-xs sm:text-sm !bg-gradient-to-r !from-emerald-500 !to-teal-500 hover:!from-emerald-400 hover:!to-teal-400 !text-white shadow-lg shadow-emerald-500/30"
              >
                {isExporting ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-4 h-4 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                )}
                <span className="hidden sm:inline">Export</span>
                <svg className="w-3 h-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </Button>
              
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 py-2 z-50 max-h-[70vh] overflow-y-auto">
                  <div className="px-3 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Raster</div>
                  <button
                    onClick={() => handleExport('png')}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3"
                  >
                    <span className="w-8 text-center text-xs font-mono bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded">PNG</span>
                    <span>Image (Transparent)</span>
                  </button>
                  
                  <div className="px-3 py-1 mt-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Pattern</div>
                  <button
                    onClick={() => handleExport('pdf')}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3"
                  >
                    <span className="w-8 text-center text-xs font-mono bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 px-1.5 py-0.5 rounded">PDF</span>
                    <span>Printable Pattern</span>
                  </button>
                  
                  <div className="px-3 py-1 mt-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Vector / Cutting</div>
                  <button
                    onClick={() => handleExport('svg')}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3"
                  >
                    <span className="w-8 text-center text-xs font-mono bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">SVG</span>
                    <span>Cricut / Silhouette</span>
                  </button>
                  <button
                    onClick={() => handleExport('xtool-svg')}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3"
                  >
                    <span className="w-8 text-center text-xs font-mono bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 px-1.5 py-0.5 rounded">SVG</span>
                    <span>xTool / LightBurn (laser)</span>
                  </button>
                  <button
                    onClick={() => handleExport('depth-luminance')}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3"
                  >
                    <span className="w-8 text-center text-xs font-mono bg-fuchsia-100 dark:bg-fuchsia-900 text-fuchsia-700 dark:text-fuchsia-300 px-1.5 py-0.5 rounded">PNG</span>
                    <span>3D Engrave depth (line art)</span>
                  </button>
                  <button
                    onClick={() => handleExport('depth-ai')}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3"
                  >
                    <span className="w-8 text-center text-xs font-mono bg-fuchsia-100 dark:bg-fuchsia-900 text-fuchsia-700 dark:text-fuchsia-300 px-1.5 py-0.5 rounded">PNG</span>
                    <span>3D Engrave depth (photo / AI)</span>
                  </button>
                  <div className="w-full px-4 py-2 text-left text-sm text-slate-400 dark:text-slate-500 flex items-center gap-3 cursor-not-allowed">
                    <span className="w-8 text-center text-xs font-mono bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 px-1.5 py-0.5 rounded">DXF</span>
                    <span>AutoCAD / Laser</span>
                    <span className="ml-auto text-[10px] bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded-full">Soon</span>
                  </div>
                  
                  <div className="px-3 py-1 mt-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Machine Embroidery</div>
                  <button
                    onClick={() => handleExportClick('pes')}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3"
                  >
                    <span className="w-8 text-center text-xs font-mono bg-violet-100 dark:bg-violet-900 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 rounded">PES</span>
                    <span>Brother / Babylock</span>
                  </button>
                  <button
                    onClick={() => handleExportClick('dst')}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3"
                  >
                    <span className="w-8 text-center text-xs font-mono bg-violet-100 dark:bg-violet-900 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 rounded">DST</span>
                    <span>Tajima / Commercial</span>
                  </button>
                  <button
                    onClick={() => handleExportClick('jef')}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3"
                  >
                    <span className="w-8 text-center text-xs font-mono bg-violet-100 dark:bg-violet-900 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 rounded">JEF</span>
                    <span>Janome / Elna</span>
                  </button>
                  <button
                    onClick={() => handleExportClick('exp')}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3"
                  >
                    <span className="w-8 text-center text-xs font-mono bg-violet-100 dark:bg-violet-900 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 rounded">EXP</span>
                    <span>Melco</span>
                  </button>
                  
                  <div className="my-1 border-t border-slate-200 dark:border-slate-700" />
                  <div className="px-3 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Share</div>
                  <button
                    onClick={() => handleGeneratePin()}
                    disabled={(!canvasHasContent && !canvasImage)}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3 disabled:opacity-40"
                  >
                    <span className="w-8 text-center text-base">📍</span>
                    <span>Generate Pinterest Pin (1000x1500)</span>
                  </button>
                  <button
                    onClick={() => handleShareToGallery()}
                    disabled={(!canvasHasContent && !canvasImage)}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3 disabled:opacity-40"
                  >
                    <span className="w-8 text-center text-base">🎨</span>
                    <span>Post to Zenna Gallery</span>
                  </button>
                  {/* Native share — only shown when Web Share API is available (mobile + some desktop) */}
                  {typeof navigator !== 'undefined' && typeof navigator.share === 'function' && (
                    <button
                      onClick={() => handleShareSocial('native')}
                      disabled={(!canvasHasContent && !canvasImage)}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3 disabled:opacity-40"
                    >
                      <span className="w-8 text-center text-base">📲</span>
                      <span>Share via phone (opens share sheet)</span>
                    </button>
                  )}
                  <button
                    onClick={() => handleShareSocial('copy')}
                    disabled={(!canvasHasContent && !canvasImage)}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3 disabled:opacity-40"
                  >
                    <span className="w-8 text-center text-base">🔗</span>
                    <span>Copy Share Link</span>
                  </button>
                  <button
                    onClick={() => handleShareSocial('pinterest')}
                    disabled={(!canvasHasContent && !canvasImage)}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3 disabled:opacity-40"
                  >
                    <span className="w-8 text-center text-base">📌</span>
                    <span>Share to Pinterest</span>
                  </button>
                  <button
                    onClick={() => handleShareSocial('facebook')}
                    disabled={(!canvasHasContent && !canvasImage)}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3 disabled:opacity-40"
                  >
                    <span className="w-8 text-center text-base">📘</span>
                    <span>Share to Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShareSocial('twitter')}
                    disabled={(!canvasHasContent && !canvasImage)}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3 disabled:opacity-40"
                  >
                    <span className="w-8 text-center text-base">𝕏</span>
                    <span>Share to X / Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShareSocial('instagram')}
                    disabled={(!canvasHasContent && !canvasImage)}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3 disabled:opacity-40"
                  >
                    <span className="w-8 text-center text-base">📸</span>
                    <span>Copy for Instagram</span>
                  </button>
                  <button
                    onClick={() => handleShareSocial('tiktok')}
                    disabled={(!canvasHasContent && !canvasImage)}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3 disabled:opacity-40"
                  >
                    <span className="w-8 text-center text-base">🎵</span>
                    <span>Copy for TikTok</span>
                  </button>
                </div>
              )}
            </div>

            {/* Zenna Go — Home */}
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-gradient-to-r from-zenna-violet to-zenna-cyan text-white hover:opacity-90 transition-all shadow-lg shadow-zenna-violet/30"
              title="Back to Dashboard"
            >
              Zenna Go
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
            </button>
          </div>
        </div>

        {/* Mobile View Toggle */}
        <div className="md:hidden flex border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={() => setMobileView('canvas')}
            className={`flex-1 py-4 text-sm font-medium transition-colors min-h-[48px] ${
              mobileView === 'canvas'
                ? 'text-zenna-violet dark:text-zenna-cyan border-b-2 border-zenna-violet dark:border-zenna-cyan bg-zenna-violet/5'
                : 'text-slate-500 dark:text-slate-400 active:bg-slate-100 dark:active:bg-slate-800'
            }`}
            aria-label="Show canvas"
          >
            <span className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Canvas
              {canvasHasContent && <span className="w-2 h-2 bg-zenna-emerald rounded-full"></span>}
            </span>
          </button>
          <button
            onClick={() => setMobileView('chat')}
            className={`flex-1 py-4 text-sm font-medium transition-colors min-h-[48px] ${
              mobileView === 'chat'
                ? 'text-zenna-violet dark:text-zenna-cyan border-b-2 border-zenna-violet dark:border-zenna-cyan bg-zenna-violet/5'
                : 'text-slate-500 dark:text-slate-400 active:bg-slate-100 dark:active:bg-slate-800'
            }`}
            aria-label="Show chat"
          >
            <span className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Chat
              {isLoading && <span className="w-2 h-2 bg-zenna-violet rounded-full animate-pulse"></span>}
            </span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* ===== CANVAS / 3D PANEL WITH TAB TOGGLE ===== */}
        <div className={`${mobileView === 'canvas' ? 'flex' : 'hidden'} md:flex flex-1 flex-col overflow-hidden canvas-editor-area`}>

          {/* 2D / Embroidery / 3D Tab Toggle */}
          <div className="flex flex-shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 z-10">
            <button
              onClick={() => setViewMode('2d')}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-all ${
                viewMode === '2d'
                  ? 'text-zenna-violet dark:text-zenna-cyan border-b-2 border-zenna-violet dark:border-zenna-cyan'
                  : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
              }`}
            >
              🎨 2D Canvas
            </button>
            <button
              onClick={() => setViewMode('embroidery')}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-all ${
                viewMode === 'embroidery'
                  ? 'text-zenna-violet dark:text-zenna-cyan border-b-2 border-zenna-violet dark:border-zenna-cyan'
                  : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
              }`}
            >
              🧵 Embroidery
            </button>
            <button
              disabled
              className="flex-1 px-4 py-2 text-sm font-medium text-slate-300 dark:text-slate-600 cursor-not-allowed relative"
              title="3D viewer coming soon!"
            >
              🧊 3D View
              <span className="ml-1 text-[9px] bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 px-1 py-0.5 rounded-full align-top">Soon</span>
            </button>
          </div>

          {/* Embroidery Editor Tab */}
          {viewMode === 'embroidery' && (
            <div className="flex-1 min-h-0 flex">
              {embroideryEditor ? (
                <EmbroideryEditor
                  previewData={embroideryEditor.previewData}
                  pesBase64={embroideryEditor.pesBase64}
                  filename={embroideryEditor.filename}
                  imageUrl={embroideryEditor.imageUrl}
                  onClose={() => setViewMode('2d')}
                  onExport={(editedData) => {
                    console.log('Export edited embroidery:', editedData)
                  }}
                  onReDigitize={(selectedShapes) => {
                    console.log('Re-digitize shapes:', selectedShapes)
                  }}
                />
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center gap-4 text-slate-400 dark:text-slate-500">
                  <svg className="w-16 h-16 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm font-medium">No embroidery project loaded</p>
                  <p className="text-xs text-slate-500 dark:text-slate-600 text-center max-w-xs">Export a design as PES to open it here, or ask Zenna to digitize an image</p>
                </div>
              )}
            </div>
          )}

          {/* 2D Canvas Editor */}
          <div className={`flex-1 min-h-0 ${viewMode === '2d' ? 'flex' : 'hidden'}`}>
            {stitchPreview ? (
              <StitchPreview
                previewData={stitchPreview.previewData}
                pesBase64={stitchPreview.pesBase64}
                filename={stitchPreview.filename}
                onClose={() => setStitchPreview(null)}
                onReExport={(deletedJumps) => {
                  console.log('Re-export with deleted jumps:', deletedJumps)
                  // Future: re-export with edited jump stitches
                }}
                onOpenEditor={() => {
                  setEmbroideryEditor({
                    previewData: stitchPreview.previewData,
                    pesBase64: stitchPreview.pesBase64,
                    filename: stitchPreview.filename,
                    imageUrl: canvasImage
                  })
                  setStitchPreview(null)
                  setViewMode('embroidery')
                  if (window.innerWidth < 768) {
                    setMobileView('canvas')
                  }
                }}
              />
            ) : (
            <CanvasEditor
              ref={canvasEditorRef}
              onCanvasReady={async () => {
                console.log('Fabric.js canvas editor ready')
                // Load any pending uploaded image that couldn't load before canvas was ready
                if (pendingCanvasImage.current && canvasEditorRef.current?.setMainImage) {
                  try {
                    await canvasEditorRef.current.setMainImage(pendingCanvasImage.current)
                    console.log('Loaded pending image onto canvas')
                    pendingCanvasImage.current = null
                  } catch (err) {
                    console.warn('Failed to load pending image:', err)
                  }
                }
              }}
              onSelectionChange={(obj) => {
                // Future: update property inspector panel
              }}
              onLayersChange={(layers) => {
                setCanvasHasContent(layers.length > 0)
              }}
              onHistoryChange={(state) => {
                // Future: show undo/redo availability in header
              }}
              onColorsExtracted={(colors) => {
                setDesignColors(colors)
              }}
            />
            )}
          </div>

          {/* 3D Viewer */}
          {viewMode === '3d' && (
            <div className="flex-1 min-h-0 overflow-hidden">
              <ThreeDViewer
                canvasRef={canvasEditorRef}
                canvasImage={canvasImage}
                initialMode={threeDMode}
                onModeChange={setThreeDMode}
              />
            </div>
          )}
        </div>

        {/* ===== RESIZABLE DIVIDER ===== */}
        <div
          onMouseDown={handlePanelResize}
          className="hidden md:flex w-1.5 cursor-col-resize items-center justify-center bg-slate-100 dark:bg-slate-700 hover:bg-zenna-violet/20 dark:hover:bg-zenna-cyan/20 active:bg-zenna-violet/30 dark:active:bg-zenna-cyan/30 transition-colors flex-shrink-0 group"
          title="Drag to resize"
        >
          <div className="w-0.5 h-8 bg-slate-300 dark:bg-slate-500 rounded-full group-hover:bg-zenna-violet dark:group-hover:bg-zenna-cyan transition-colors"></div>
        </div>

        {/* Chat Panel */}
        <div 
          className={`${mobileView === 'chat' ? 'flex' : 'hidden'} md:flex flex-col bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 relative flex-shrink-0`}
          style={{ width: window.innerWidth >= 768 ? chatPanelWidth : '100%' }}
        >
          <div className="hidden md:block p-4 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <img 
                src="/zenna-avatar.webp" 
                alt="Zenna" 
                className="w-10 h-10 rounded-full object-cover border-2 border-zenna-violet/30"
              />
              <div>
                <h3 className="font-display font-semibold text-slate-800 dark:text-white">Zenna</h3>
                <p className="text-xs text-emerald-500 flex items-center gap-1">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                  Ready to help
                </p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div ref={chatScrollRef} onScroll={handleChatScroll} className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`group flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} ${message.role === 'assistant' ? 'items-start gap-2' : 'items-start gap-1.5'}`}
              >
                {/* Edit button for user messages (hover to reveal) */}
                {message.role === 'user' && message.content && (
                  <button
                    onClick={() => handleEditMessage(message.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity mt-2 p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                    title="Edit and resend"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                )}
                {/* Zenna Avatar — only for assistant messages */}
                {message.role === 'assistant' && (
                  <img
                    src="/zenna-avatar.webp"
                    alt="Zenna"
                    className="w-8 h-8 rounded-full object-cover flex-shrink-0 border-2 border-zenna-violet/30"
                  />
                )}
                <div
                  className={`max-w-[85%] sm:max-w-[80%] rounded-2xl px-3 sm:px-4 py-2.5 sm:py-3 ${
                    message.role === 'user'
                      ? 'bg-gradient-zenna text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200'
                  }`}
                >
                  {message.role === 'assistant' ? renderMessageContent(message.content) : <p className="text-sm whitespace-pre-wrap">{message.content}</p>}
                  
                  {/* Copy button for assistant messages */}
                  {message.role === 'assistant' && message.content && (
                    <button
                      onClick={() => copyMessageText(message.content, message.id)}
                      className="mt-2 flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                      title="Copy message"
                    >
                      {copiedMessageId === message.id ? (
                        <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Copied!</>
                      ) : (
                        <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg> Copy</>
                      )}
                    </button>
                  )}
                  
                  {message.images && message.images.length > 0 && (
                    <div className="mt-3">
                      {message.images.length >= 3 && message.images.length <= 6 ? (
                        <>
                          {/* Detect reference mode: any image marked isReference means this whole set is real maker photos */}
                          {(() => {
                            const isReferenceSet = message.images.some(img => typeof img === 'object' && img.isReference)
                            return (
                              <>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1.5">
                                  {isReferenceSet ? (
                                    <>
                                      <span className="inline-block w-1.5 h-1.5 bg-amber-400 rounded-full"></span>
                                      <span className="font-medium text-amber-500 dark:text-amber-400">Real maker photos</span>
                                      <span className="text-slate-400">· click to view source</span>
                                    </>
                                  ) : (
                                    'Tap to load onto canvas:'
                                  )}
                                </p>
                                <div className={`grid gap-2 ${message.images.length === 4 ? 'grid-cols-2' : message.images.length <= 3 ? 'grid-cols-3' : 'grid-cols-3'}`}>
                                  {message.images.map((image, index) => {
                                    const imageUrl = typeof image === 'string' ? image : image.url
                                    const refInfo = typeof image === 'object' ? image.referenceInfo : null
                                    const isRef = typeof image === 'object' && image.isReference
                                    return (
                                      <div
                                        key={index}
                                        className={`relative cursor-pointer rounded-lg overflow-hidden transition-all ${
                                          canvasImage === imageUrl && !isRef
                                            ? 'ring-2 ring-zenna-emerald ring-offset-2 dark:ring-offset-slate-700'
                                            : 'hover:opacity-90'
                                        }`}
                                        onClick={() => {
                                          if (isRef && refInfo?.sourcePage) {
                                            window.open(refInfo.sourcePage, '_blank', 'noopener')
                                          } else {
                                            handleImageClick(imageUrl, index, message.id)
                                          }
                                        }}
                                      >
                                        <img src={imageUrl} alt={refInfo?.title || `Option ${index + 1}`} className="w-full aspect-square object-cover" />
                                        <div className={`absolute top-1 left-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                                          canvasImage === imageUrl && !isRef ? 'bg-zenna-emerald text-white' : 'bg-black/50 text-white'
                                        }`}>
                                          {index + 1}
                                        </div>
                                        {isRef && (
                                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1.5">
                                            <div className="text-[9px] text-amber-300 font-semibold uppercase tracking-wider">Reference</div>
                                            {refInfo?.source && (
                                              <div className="text-[10px] text-white/90 truncate">{refInfo.source}</div>
                                            )}
                                          </div>
                                        )}
                                        {!isRef && canvasImage === imageUrl && (
                                          <div className="absolute bottom-1 right-1 bg-zenna-emerald text-white text-xs px-1.5 py-0.5 rounded">
                                            ✓ On canvas
                                          </div>
                                        )}
                                      </div>
                                    )
                                  })}
                                </div>
                                {isReferenceSet && (
                                  <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-2 italic">
                                    These are photos from real makers — I've written your custom pattern below based on what you asked for.
                                  </p>
                                )}
                              </>
                            )
                          })()}
                        </>
                      ) : (
                        <div className="space-y-2">
                          {message.images.map((image, index) => {
                            const imageUrl = typeof image === 'string' ? image : image.url
                            const imagePrompt = typeof image === 'string' ? '' : image.prompt
                            const isUpload = typeof image === 'object' && image.isUpload
                            return (
                              <div key={index} className="relative group cursor-pointer" onClick={() => handleImageClick(imageUrl, index, message.id)}>
                                <img
                                  src={imageUrl}
                                  alt={imagePrompt || 'Image'}
                                  className={`rounded-lg w-full hover:opacity-90 transition-opacity border-2 ${isUpload ? 'border-zenna-cyan/50' : 'border-white/20'}`}
                                />
                                {isUpload && (
                                  <div className="absolute top-1 right-1 bg-zenna-cyan text-white text-xs px-1.5 py-0.5 rounded pointer-events-none">
                                    📷 Uploaded
                                  </div>
                                )}
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-lg pointer-events-none">
                                  <span className="text-white text-xs font-medium px-2 py-1 bg-black/50 rounded">
                                    Tap to edit on canvas
                                  </span>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start items-start gap-2">
                {/* Zenna Avatar for loading state */}
                <img 
                  src="/zenna-avatar.webp" 
                  alt="Zenna" 
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0 border-2 border-zenna-violet/30"
                />
                <div className="bg-slate-100 dark:bg-slate-700 rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-slate-500 dark:text-slate-400 mr-2 min-w-[160px] transition-opacity duration-300">
                      {thinkingMessages[thinkingIndex]}
                    </span>
                    <div className="w-2 h-2 bg-zenna-violet rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-zenna-cyan rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-zenna-emerald rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    <button
                      onClick={handleStop}
                      className="ml-3 px-2.5 py-1 text-[10px] font-medium text-red-500 hover:text-white hover:bg-red-500 border border-red-300 dark:border-red-600 rounded-full transition-colors"
                      title="Stop generating"
                    >
                      ■ Stop
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Scroll-to-bottom button */}
          {showScrollBtn && (
            <button
              onClick={scrollToBottom}
              className="absolute bottom-32 right-6 z-10 w-8 h-8 rounded-full bg-slate-700 dark:bg-slate-500 text-white shadow-lg flex items-center justify-center hover:bg-zenna-violet transition-colors"
              title="Scroll to bottom"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
            </button>
          )}

          {/* Upload Preview — supports multiple files */}
          {uploadedFiles.length > 0 && (
            <div className="px-3 sm:px-4 pt-2">
              <div className="flex flex-wrap gap-2 items-center">
                {uploadedFiles.map((file, idx) => (
                  <div key={idx} className="relative group">
                    {file.category === 'image' && file.dataUrl ? (
                      <img src={file.dataUrl} alt={file.name} className="h-16 rounded-lg border-2 border-zenna-cyan object-cover" />
                    ) : (
                      <div className="h-16 px-3 rounded-lg border-2 border-zenna-violet/50 bg-slate-50 dark:bg-slate-700 flex items-center gap-2">
                        <span className="text-lg">{file.category === 'document' ? '📄' : '📝'}</span>
                        <div className="flex flex-col">
                          <span className="text-xs font-medium text-slate-700 dark:text-slate-200 max-w-[120px] truncate">{file.name}</span>
                          <span className="text-[10px] text-slate-400">
                            {file.textContent ? `${file.textContent.length.toLocaleString()} chars` : 'PDF'}
                          </span>
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => clearUpload(idx)}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] hover:bg-red-600 opacity-80 group-hover:opacity-100 transition-opacity"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {uploadedFiles.length < 5 && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="h-16 w-16 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center text-slate-400 hover:border-zenna-violet hover:text-zenna-violet transition-colors"
                    title="Add more files"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-3 sm:p-4 border-t border-slate-200 dark:border-slate-700">
            <div className="flex gap-2">
              {/* Upload button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
                className="p-2.5 sm:p-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
                title="Upload files — images, PDFs, text docs, code"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
              </button>

              {/* Voice input button (V2.0) */}
              <button
                onClick={toggleVoiceInput}
                disabled={isLoading}
                className={`p-2.5 sm:p-3 rounded-xl border transition-colors disabled:opacity-50 ${
                  isListening
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700 text-red-500 animate-pulse'
                    : 'border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600'
                }`}
                title={isListening ? 'Stop listening' : 'Voice input'}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {isListening
                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                    : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  }
                </svg>
              </button>
              
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value)
                  // Auto-grow: adjust height to content, min 2 rows, max 8 rows
                  const el = e.target
                  el.style.height = 'auto'
                  const lineHeight = 20
                  const minH = lineHeight * 2 + 16 // 2 rows + padding
                  const maxH = lineHeight * 8 + 16 // 8 rows + padding
                  el.style.height = Math.min(Math.max(el.scrollHeight, minH), maxH) + 'px'
                }}
                onKeyDown={handleKeyDown}
                placeholder={uploadedFiles.length > 0 ? "What would you like me to do with these files?" : "Describe your design idea..."}
                className="flex-1 resize-y rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 sm:px-4 py-2.5 sm:py-3 text-sm text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-zenna-violet/50 focus:border-zenna-violet"
                rows={2}
                style={{ minHeight: '56px', maxHeight: '200px' }}
                disabled={isLoading}
              />
              <Button
                onClick={isLoading ? handleStop : handleSend}
                disabled={!isLoading && (!input.trim() && uploadedFiles.length === 0)}
                className={`!p-2.5 sm:!p-3 ${isLoading ? '!bg-red-500 hover:!bg-red-600' : ''}`}
                title={isLoading ? 'Stop generating' : 'Send message'}
              >
                {isLoading ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="6" width="12" height="12" rx="1" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
              </Button>
            </div>
            
            {/* Quick Share Bar */}
            <div className="flex items-center justify-center gap-1 mt-2 flex-wrap">
              <span className="text-[10px] text-slate-400 mr-1">Share:</span>
              {[
                // Native share (phone share sheet) — only on devices that support it
                ...(typeof navigator !== 'undefined' && typeof navigator.share === 'function'
                  ? [{
                      id: 'native',
                      label: 'Share sheet',
                      color: 'text-slate-500 dark:text-slate-400',
                      icon: (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M16 8l-4-4m0 0L8 8m4-4v13" />
                        </svg>
                      )
                    }]
                  : []),
                {
                  id: 'facebook',
                  label: 'Facebook',
                  color: 'text-[#1877F2]',
                  icon: (
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  )
                },
                {
                  id: 'pinterest',
                  label: 'Pinterest',
                  color: 'text-[#E60023]',
                  icon: (
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
                    </svg>
                  )
                },
                {
                  id: 'twitter',
                  label: 'X',
                  color: 'text-slate-700 dark:text-slate-200',
                  icon: (
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  )
                },
                {
                  id: 'instagram',
                  label: 'Instagram',
                  color: 'text-[#E4405F]',
                  icon: (
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  )
                },
                {
                  id: 'tiktok',
                  label: 'TikTok',
                  color: 'text-slate-700 dark:text-slate-200',
                  icon: (
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.321 5.562a5.124 5.124 0 0 1-.443-.258 6.228 6.228 0 0 1-1.137-.966c-.849-.971-1.166-1.956-1.282-2.645h.004C16.368.989 16.4 0 16.428 0h-3.42v13.22c0 .18 0 .357-.006.533l-.002.067c-.004.1-.01.2-.018.299v.048a4.016 4.016 0 0 1-3.99 3.784 4.013 4.013 0 0 1 0-8.026c.213 0 .422.015.627.046v-3.48a7.489 7.489 0 0 0-.627-.03 7.435 7.435 0 0 0-5.268 12.686 7.435 7.435 0 0 0 12.708-5.268V7.016a9.12 9.12 0 0 0 5.321 1.708v-3.41a5.167 5.167 0 0 1-3.432-.75z"/>
                    </svg>
                  )
                },
                {
                  id: 'copy',
                  label: 'Copy Link',
                  color: 'text-slate-500 dark:text-slate-400',
                  icon: (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  )
                },
              ].map(p => (
                <button
                  key={p.id}
                  onClick={() => handleShareSocial(p.id)}
                  disabled={!canvasHasContent && !canvasImage}
                  className={`p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 active:bg-slate-200 dark:active:bg-slate-600 transition-colors disabled:opacity-30 min-w-[36px] min-h-[36px] flex items-center justify-center ${p.color}`}
                  title={p.label}
                  aria-label={p.label}
                >
                  {p.icon}
                </button>
              ))}
            </div>
            
            <ChatFooterStats />
            <p className="text-[10px] text-slate-400/70 mt-0.5 text-center">
              Zenna is AI-powered and can make mistakes. Please double-check important information. As an Amazon Associate, Zenna earns from qualifying purchases.
            </p>
          </div>
        </div>
      </div>

      {/* Safety Warning Banner */}
      <div className="bg-red-600 text-white text-center py-1 px-4 text-[10px] flex-shrink-0">
        ⚠️ Craft projects only. Suspicious requests may be logged.
      </div>
    </div>
  )
}

export default Workspace